// Type-only edges out of this file. `secret-descriptions.ts` owns what
// a stored secret's description IS (and the rule for resolving one), and it
// imports nothing itself - so naming it here adds no runtime dependency and no
// cycle, where redefining the shape would let the wire and the resolver drift.
import type { SecretMeta } from '../helpers/board-agent-core/secret-descriptions';
import type {
  LocalAiMachine,
  LocalAiModelFit,
  LocalAiModelId
} from '../helpers/localAiModels';
import type { CliAskSettings } from '../validators/oakboxBackend';
import type {
  LocalAiActPayload,
  LocalAiSearchPayload,
  MachineCleanupPayload,
  RunnerModuleAction,
  RunnerModuleId,
  RunnerPartAction,
  RunnerPartId,
  RunnerPartsActPayload
} from '../validators/runnerParts';
import type {
  RunnerCategorizePayload,
  RunnerCategoryTree
} from '../validators/runnerVault';

export type {
  LocalAiActPayload,
  LocalAiSearchPayload,
  MachineCleanupPayload,
  RunnerCategorizePayload,
  RunnerModuleAction,
  RunnerModuleId,
  RunnerPartAction,
  RunnerPartId,
  RunnerPartsActPayload
};

// ── Board sync broadcast payload ──
// Multi-device sync: clients broadcast one of these after a mutation, and every
// other client connected to the same runner invalidates its query caches.
export type BoardUpdatePayload =
  | { scope: 'factiiis' }
  | { scope: 'posts'; factiiiId: number }
  | { scope: 'all' };

// ── Session types (shared with renderer) ──

/** CLI agent a space runs for AI work (ask, cards, deploy, one-shots).
 *  Per-space config: the runner's default board seeds it, boards override. */
export type AgentProvider = 'claude' | 'codex';

/** 'claude' here means the AI pipeline mode (runs the space's configured
 *  provider), as opposed to the user-driven 'bare' terminal mode. */
export type CardAIMode = 'claude' | 'bare';

/** What a CLI is doing, as the factiii plugin's hooks report it: `working` on
 *  UserPromptSubmit, `idle` on Stop, `blocked` on a permission request. */
export type AgentState = 'working' | 'idle' | 'blocked';

export type CardAISessionPhase =
  | 'starting'
  | 'research'
  | 'awaiting-input'
  | 'implement'
  | 'review'
  | 'complete'
  | 'error'
  | 'done'
  | 'bare-active'
  | 'bare-review';

export type CardAITodo = {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
};

// ── Agent skills (SKILL.md) ──

/** State of the one symlink Claude Code needs: `.claude/skills` must point at
 *  `.agents/skills`. `blocked` means a real directory sits there, so the skills
 *  inside it must move before a link can replace it. */
export type SkillLinkState = 'linked' | 'missing' | 'blocked';

/** `global` = the runner host's own home dir, shared by every space it drives.
 *  `repo` = committed to the space's repo, changeable only through a merge. */
export type SkillScope = 'global' | 'repo';

/** A repo skill's state relative to the default branch. Global skills are
 *  always `live`: writing one applies it immediately. */
export type SkillStatus =
  | 'live' // on the default branch: what the agents actually load
  | 'pending-new' // exists only on an unmerged skill/* branch
  | 'pending-edit'; // on both, and they differ

export type SkillSummary = {
  name: string;
  scope: SkillScope;
  description: string;
  /** Absolute for global skills, repo-relative for repo skills. */
  dir: string;
  status: SkillStatus;
  /** The unmerged branch carrying it, when status is not `live`. */
  pendingBranch: string;
  /** Frontmatter problems. The skill still opens so it can be fixed. */
  errors: string[];
};

export type CardAILogEntry = {
  time: number;
  type: 'system' | 'text' | 'tool' | 'error' | 'text-append' | 'thinking';
  content: string;
  /** On a Board AI log line: the conversation it streams into. Several can
   *  answer at once on the one board log channel. Absent from a runner older
   *  than conversations, and from every other log. */
  conversationId?: string;
};

/** A session's worktree, counted against the branch it was cut from. Cheap
 *  enough to keep on every summary: counts only, never a diff. */
export type SessionGitSummary = {
  /** Commits on HEAD that the base does not have. */
  ahead: number;
  /** Commits on the base since the work branched off. */
  behind: number;
  /** Lines added and removed since the merge base, uncommitted edits
   *  included. Untracked files are not counted until git knows them. */
  added: number;
  removed: number;
  /** Paths with uncommitted changes, untracked included. */
  dirty: number;
  /** Commits no remote has. */
  unpushed: number;
};

export type CardAISessionSummary = {
  taskId: string;
  postId: string;
  phase: CardAISessionPhase | null;
  mode: CardAIMode;
  step: 1 | 2 | 3;
  diff: string;
  changedFiles: string[];
  commitMessage: string;
  previewUrl: string;
  previewRunning: boolean;
  reproSteps: string;
  pendingQuestion: string;
  error: string;
  startedAt: number;
  /** Absent when the plugin is not installed or has not fired yet, in which
   *  case the dot falls back to "alive". */
  agentState?: AgentState;
  /** The public links this terminal opened (`factiii-expose`), so the sessions
   *  list can show each beside the terminal that made it. Absent from a runner
   *  too old to report them, and for anything that is not a terminal. */
  exposed?: ExposedPort[];
  /** One line of what the CLI is doing, from the factiii plugin's hooks: the
   *  tool it is about to run, or the first line of its last reply. Absent
   *  until a hook fires, and from a runner too old to report it. */
  agentActivity?: string;
  /** How far the work in this terminal's worktree has got. Absent until the
   *  first read, for a session with no worktree, and from an older runner. */
  git?: SessionGitSummary;
  targetBranch: string;
  taskTitle: string;
  todos: CardAITodo[];
  /**
   * The provider CLI's own conversation id (claude session UUID / codex
   * thread id), threaded across the research→implement→review spawns. Each
   * spawn is headless `-p` print mode and exits, so there's no live process
   * to attach to — but this id resumes the thread interactively from a shell
   * in the same workspace and cwd. Empty until the first spawn reports one.
   */
  agentSessionId: string;
  /** The CLI that ran this session (per-job; may differ from the space
   *  default). A terminal must resume with THIS provider, not config's. */
  provider: AgentProvider;
};

export type CardAISessionFull = CardAISessionSummary & {
  logs: CardAILogEntry[];
};

// ── Runner activity (everything the runner does that ISN'T a card session) ──

/**
 * Long-running space-level work: Ask Board, deploys, setup, indexing, sign-ins.
 * Sessions are card-keyed and open a terminal; these are not, and each belongs
 * to its own modal — so they ride their own channel rather than being bent
 * into `CardAISessionSummary`.
 */
export type RunnerActivityKind =
  | 'ask'
  | 'deploy-run'
  | 'deploy-skill'
  | 'deploy-env'
  | 'setup'
  | 'onedrive-index'
  | 'secrets-auth'
  /** A member's call of a dangerous job waiting on the runner owner's yes
   *  (owner-approval.ts). One row per request, keyed by its id. */
  | 'dangerous-approval'
  | 'signin';

/** One command waiting on the owner's store password, as the global
 *  authorization modal renders it. No secret value ever appears here: a
 *  command string, the key NAMES it will receive, and which session asked. */
export type PendingSecretsAuth = {
  /** Identifies this prompt across the wire, so a stale client cannot answer
   *  a request that has already been replaced by the next one. */
  id: string;
  command: string;
  /** The keys this command will receive, and nothing else in the store. */
  keys: string[];
  /** Which session asked - "Deploy run", "Terminal · <card>". Never blank:
   *  an unattributed request is refused before it ever becomes a prompt. */
  origin: string;
  /** A previous attempt's failure (wrong password), shown on the re-prompt. */
  error: string;
  requestedAt: number;
  /** Who asked, server-vouched (a job call or an inbox write on a shared
   *  runner). Absent for a free command from the owner's own session. */
  caller?: { userId: string; name: string };
  /** The job this spawn belongs to. Only a job may be covered by an unlock
   *  window, so the prompt offers a duration only when this is set. */
  job?: string;
  /** Set when approving writes a secret from the inbox rather than running a
   *  command: which key, env value or file blob, and whether it replaces one. */
  inbox?: { id: string; key: string; kind: 'env' | 'file'; replaces: boolean };
};

/** One member's call of a dangerous job that
 *  waits for the runner owner to approve it, because the owner has not given
 *  that member the Dangerous jobs permission. Everything the owner needs to
 *  decide, in full: the one command line that will run, with every param
 *  filled in. A param is a validated, caller-chosen string, never a secret,
 *  so it may be shown whole (an SQL statement, say). */
export type PendingDangerousApproval = {
  /** Identifies this request across the wire. */
  id: string;
  job: string;
  /** The job's `command` from jobs.json with each param filled in and
   *  quoted, plus any param the command reads only from its env as a leading
   *  `JOB_PARAM_X='…'` (jobs.ts filledCommand). Never cut. */
  command: string;
  /** Who asked, server-vouched. */
  caller: { userId: string; name: string };
  /** The board the call runs on. */
  spaceSlug: string;
  requestedAt: number;
  /** When it ends as denied if nobody answers (epoch ms). */
  expiresAt: number;
};

/** Who opened the current unlock window and until when (ISO). */
export type SecretsUnlockWindow = {
  until: string;
  byUserId: string;
  byName: string;
};

/** One line of the runner's append-only audit log
 *  (`~/.factiii-runner/<space>/audit.jsonl`). Names only - never a value, the
 *  password, or a decrypted file path. `prev` is the sha256 of the line before
 *  it, so an edited or deleted line breaks the chain. */
export type AuditEntry = {
  ts: string;
  actor: { userId: string; name: string };
  via: 'api' | 'data-channel' | 'owner-terminal';
  action:
    | 'job'
    | 'job-end'
    | 'job-cancel'
    /** A free `factiii-secrets run` from a session on the runner. */
    | 'run'
    | 'unlock'
    | 'lock'
    | 'secret-set'
    | 'secret-unset'
    | 'password-rotate'
    /** The owner took an offline backup of the board's secrets folder. */
    | 'backup-export'
    /** The owner read a backup file's secrets in the app. `keys` names them. */
    | 'backup-open'
    /** The owner imported secrets from a backup file into this board. `keys`
     *  names what was written. */
    | 'backup-import'
    | 'shared-change'
    /** A wrong runner password, or one refused while the guess limit's
     *  backoff runs (password-throttle.ts). `actor` is who typed it. */
    | 'auth-failed'
    | 'denied'
    /** The runner owner approved one member's call of a dangerous job, or
     *  denied it (or nobody answered in time: `detail` says which). `command`
     *  is the filled command line the owner saw, params and all
     *  (jobs.ts filledCommand); `detail` names the caller. */
    | 'dangerous-approve'
    | 'dangerous-deny'
    /** The owner cleared the log: the first line of a new chain. The old
     *  file is kept beside it on the runner; `detail` says how many entries
     *  it held and its name. */
    | 'audit-clear';
  job: string | null;
  /** Key names (and file-blob names) only. */
  keys: string[];
  /** The resolved command from the job definition, never its output. */
  command: string | null;
  exitCode: number | null;
  durationMs: number | null;
  /** Who opened the window this ran in, when it ran inside one. */
  unlockedBy: string | null;
  /** Free-form, value-free context (e.g. "inbox", "shared=true"). */
  detail?: string;
  prev: string;
};

/** One key inside an env file secret, as the app may see it. */
export type SecretFileEnvKey = {
  name: string;
  /** Never the value: its last four characters, or only dots for a short
   *  one (`maskedSecret`). */
  masked: string;
  /** False for a value with line breaks (a private key): the app does not
   *  change or remove it, the whole file is replaced. */
  editable: boolean;
};

export type RunnerJobInfo = {
  name: string;
  description: string;
  keys: string[];
  files: string[];
  /** Param name -> the regex its value must match. */
  params: Record<string, string>;
  /** The owner wants to see each call (jobs.json marks it): a member without
   *  the owner's Dangerous jobs permission waits for the runner owner to
   *  approve each call (owner-approval.ts). Absent from an older runner,
   *  which does not gate on it. */
  dangerous?: boolean;
};

/** The runner's answer to `attention`. Names and booleans only. */
export type RunnerAttention = {
  /** Keys the deploy contract or a job needs that the store does not hold.
   *  Null when the runner cannot tell (no repository, or no clone yet). */
  missingSecrets: string[] | null;
  /** Jobs that would fail for a missing key or file. */
  blockedJobs: string[];
  jobsCount: number;
  /** No unlock window is open. Locked is the store's resting state. */
  secretsLocked: boolean;
  repoConnected: boolean;
};

/** The runner's own always-on service (`factiii-runner service`), as Runner
 *  Settings > Setup shows it. Names and booleans, no path. */
export type RunnerServiceState = {
  /** False where this runner cannot install a service: `reason` says why. */
  supported: boolean;
  reason?: string;
  /** The launchd label, systemd unit or task name. */
  name: string;
  /** `foreign`: a file of that name that the runner did not write. */
  installed: 'yes' | 'no' | 'foreign';
  /** Known to the service manager, so it starts at the next login. */
  loaded: boolean;
  /** The service is what runs this runner now. Removing it from the app then
   *  leaves this runner running until the next restart. */
  thisRunner: boolean;
};

/** What `serviceInstall` / `serviceRemove` did, and the state after it. */
export type RunnerServiceOutcome = {
  ok: boolean;
  lines: string[];
  state: RunnerServiceState;
};

/** A part action the runner is doing, or did last, in the background. */
export type RunnerPartJob = {
  action: RunnerPartAction;
  state: 'running' | 'done' | 'failed';
  /** The newest lines of its output, oldest first. */
  lines: string[];
  startedAt: number;
};

/** One installable part of the runner (`factiii-runner parts`), as
 *  Runner Settings > Setup > Environment shows it. Names, numbers and command lines; no
 *  path, port or key. */
export type RunnerPart = {
  id: RunnerPartId;
  title: string;
  description: string;
  /** Every requirement is met on this machine. Detected, not stored. */
  installed: boolean;
  /** Null for a part with nothing to run. */
  running: boolean | null;
  /** Titles of the requirements that are not met. */
  missing: string[];
  /** What only a person can do for a missing requirement: a command that
   *  needs an administrator, or a settings pane. */
  manual: string[];
  /** Estimates, in MB: on disk once installed, and in memory while used. */
  diskMb: number;
  memoryMb: number;
  /** Measured now, for a part whose process is running. */
  memoryNowMb: number | null;
  /** The connector: always there, never removed. */
  core: boolean;
  actions: RunnerPartAction[];
  job: RunnerPartJob | null;
};

/** A whole module's add or remove: its sub-parts one at a time, in order. */
export type RunnerModuleJob = {
  action: RunnerModuleAction;
  state: 'running' | 'done' | 'failed';
  /** The sub-part being worked on, or the one that failed. Its own `job`
   *  carries the lines. */
  partId: RunnerPartId | null;
  startedAt: number;
};

/** A group of parts installed together or one by one (Bare, Coding, Private
 *  vault for Oakbox, Local AI). Defined once in the runner (parts.ts). */
export type RunnerPartsModule = {
  id: RunnerModuleId;
  title: string;
  description: string;
  /** Bare: the connector, never removed. */
  core: boolean;
  /** Its sub-parts on this machine, in install order. */
  parts: RunnerPart[];
  actions: RunnerModuleAction[];
  job: RunnerModuleJob | null;
};

export type RunnerPartsState = {
  /** False where parts cannot be managed (the desktop app). */
  supported: boolean;
  reason?: string;
  /** The flat list, for apps from before modules: only the part ids those
   *  apps know (the Vault as one part). */
  parts: RunnerPart[];
  /** Absent from a runner from before modules: the app then shows `parts`. */
  modules?: RunnerPartsModule[];
  /** The Local AI module's model: the machine, the pick and why. Absent
   *  from an older runner and where Local AI is not offered. */
  localAi?: RunnerLocalAi;
  /** Free disk and memory pressure. Absent from an older runner. */
  health?: RunnerMachineHealth;
};

/** `warn` comes before `critical`: at critical, adds, model pulls and vault
 *  uploads are refused. */
export type RunnerHealthLevel = 'ok' | 'warn' | 'critical';

/** One disk the runner writes to. Names what it holds, never a path. */
export type RunnerDiskHealth = {
  /** "Runner folder", "Vault", "Ollama models": the ones on this disk. */
  uses: string[];
  freeGb: number;
  totalGb: number;
  level: RunnerHealthLevel;
};

/** The machine's free disk and memory pressure, read on every list. */
export type RunnerMachineHealth = {
  /** The worst of the disks and the memory. */
  level: RunnerHealthLevel;
  disks: RunnerDiskHealth[];
  /** Null where the runner cannot read the pressure. */
  memory: RunnerHealthLevel | null;
  /** Processes the runner's OS user has against the OS's per-user limit.
   *  At the limit nothing new starts: no SSH, no login, no job, no update.
   *  Absent from a runner older than this field; null where it cannot read
   *  the limit. */
  processes?: RunnerProcessHealth | null;
  /** CPU load. Absent from an older runner; null where it cannot read it. */
  cpu?: RunnerCpuHealth | null;
  /** Memory in use, for the Status tab's reading beside the pressure
   *  level. Absent from an older runner; null where it cannot read it. */
  memoryUse?: RunnerMemoryUse | null;
  /** What is running on the machine for every board: Docker containers and
   *  servers listening on a port. Absent from an older runner. */
  services?: RunnerService[];
  /** The disk levels, in GB free: below `warnGb` warns, below
   *  `criticalGb` refuses. */
  warnGb: number;
  criticalGb: number;
};

/** The 1-minute load average against the core count. */
export type RunnerCpuHealth = {
  /** The 1-minute load average divided by the cores, as a share (1 = every
   *  core busy). */
  load: number;
  cores: number;
  level: RunnerHealthLevel;
};

/** Memory in use (what the OS would have to free for a new program), in GB. */
export type RunnerMemoryUse = {
  usedGb: number;
  totalGb: number;
};

/** One thing running on the runner's machine that every board shares. */
export type RunnerService = {
  /** `docker`: a container. `port`: a process of the runner's user listening
   *  on a TCP port (a dev server, Redis, Postgres run outside Docker). */
  kind: 'docker' | 'port';
  /** The container's name, or the listening command ("node", "redis-server"). */
  name: string;
  /** The image and its status ("postgres:16 · Up 3 days"), or the address
   *  ("127.0.0.1:6379"). */
  detail: string;
  running: boolean;
  /** The TCP ports it serves, lowest first. */
  ports: number[];
};

/** The runner user's process count against the OS's per-user cap. */
export type RunnerProcessHealth = {
  used: number;
  max: number;
  level: RunnerHealthLevel;
};

/** One thing Free up space would remove: something the runner made and can
 *  make again. Never a vault file, a secret or a model in use. */
export type RunnerCleanupItem = {
  id: string;
  title: string;
  /** Under the runner folder ("factiii-board/builds/59374f079"), or the
   *  Ollama model's name. */
  path: string;
  bytes: number;
};

/** A dry run (`ran: false`) or what a confirmed run did, and what is left. */
export type RunnerCleanup = {
  items: RunnerCleanupItem[];
  bytes: number;
  ran: boolean;
  lines: string[];
};

/** What Local AI read off the machine, the model it picked and why, and
 *  every model in the table with whether it fits. */
export type RunnerLocalAi = {
  machine: LocalAiMachine;
  /** The largest model that fits; null when the machine is too small. */
  recommended: LocalAiModelId | null;
  /** The owner's pick, else the recommended one. */
  picked: LocalAiModelId | null;
  /** "16 GB unified memory (Apple M3 Pro), so an 8B model at 4-bit, about
   *  4.9 GB." Or that the machine is too small, pointing to Claude/Codex. */
  reason: string;
  /** The picked model is in Ollama on this machine. */
  pulled: boolean;
  models: LocalAiModelFit[];
};

/** A pull or a remove of one Ollama model, from Oakbox > Account > Backend. */
export type RunnerLocalAiJob = {
  action: LocalAiActPayload['action'];
  tag: string;
  state: 'running' | 'done' | 'failed';
  /** The newest lines of its output, oldest first. */
  lines: string[];
  startedAt: number;
};

/** A runner's models as Oakbox > Account > Backend manages them
 *  (.specs/desktop-ai.md "Oakbox's backend"). Names and numbers only. */
export type RunnerLocalAiModels = {
  /** Ollama is installed. Without it nothing can be pulled. */
  ollama: boolean;
  /** Every model Ollama holds, as `ollama list` names it. */
  installed: string[];
  /** Null when the machine could not be read. */
  machine: LocalAiMachine | null;
  recommended: LocalAiModelId | null;
  /** The fixed table, each with whether it fits this machine. */
  models: LocalAiModelFit[];
  health: RunnerMachineHealth | null;
  /** The last pull or remove, kept until the next one. */
  job: RunnerLocalAiJob | null;
};

/** One model in ollama.com's search, as the runner read it off the page. */
export type OllamaLibraryModel = {
  /** `name`, or `namespace/name` for someone's own model. */
  name: string;
  description: string;
  /** "tools", "thinking", "vision", "embedding"… */
  capabilities: string[];
  /** Its parameter sizes: "0.6b", "8b"… */
  sizes: string[];
  /** Runs on Ollama's cloud, not on the machine. */
  cloud: boolean;
  downloads: number | null;
};

/** One tag of a model on ollama.com, with whether it fits this runner. Tags
 *  that are the same file are one, with the others as `aliases`. */
export type OllamaLibraryTag = {
  tag: string;
  aliases: string[];
  /** Its download size; null for a cloud tag. */
  sizeGb: number | null;
  /** "40K", "128K". */
  context: string | null;
  /** "Text", "Text, Image". */
  input: string | null;
  cloud: boolean;
  fits: boolean;
  /** Why it does not fit, or null. */
  blocker: string | null;
};

/** One CLI's sign-in on a runner, runner-wide (`cliSignins`). */
export type RunnerCliSignin = {
  /** On the runner's PATH. */
  installed: boolean;
  signedIn: boolean;
  /** The last sign-in started from Oakbox: running until the CLI settles,
   *  then how it ended. Null when none was started since the runner did. */
  flow: {
    state: 'running' | 'complete' | 'error';
    message: string | null;
  } | null;
};

export type RunnerCliSignins = Record<'claude' | 'codex', RunnerCliSignin>;

/** What `localAiSearch` answers: a query's models, or one model's tags. */
export type RunnerLocalAiSearch =
  | { kind: 'models'; query: string; models: OllamaLibraryModel[] }
  | { kind: 'tags'; name: string; tags: OllamaLibraryTag[] };

export type RunnerJobState =
  | 'queued'
  // A member's call of a dangerous job, held until the runner owner
  // approves it; denied on a no or after 10 minutes without an answer.
  | 'waiting-owner'
  | 'waiting-auth'
  | 'running'
  // Server only: the runner got the call but did not confirm it in time, so
  // it may be running. Its later reports still move the row, cancel still
  // reaches it, and nothing sends it again on its own.
  | 'unknown'
  | 'done'
  | 'denied'
  | 'failed'
  | 'cancelled';

export type SecretsInboxItem = {
  id: string;
  key: string;
  kind: 'env' | 'file';
  replaces: boolean;
  byUserId: string;
  byName: string;
  at: string;
};

/** 'working' spins; the other two are stationary and want attention. */
export type RunnerActivityState = 'working' | 'awaiting-input' | 'error';

export type RunnerActivity = {
  /** Stable per space+kind (+ discriminator for sign-ins, which can overlap),
   *  so an update replaces its row instead of stacking another one. */
  id: string;
  kind: RunnerActivityKind;
  /** null means finished — the client drops the row. */
  state: RunnerActivityState | null;
  /** The row's one line, e.g. "Ask Board" or "Deploying". */
  label: string;
  /** Current step, shown in the tooltip, e.g. "cloning repo". */
  detail: string;
  startedAt: number;
};

/** Rows the user may close from Running jobs or the session rail: a sign-in
 *  (closing it cancels the flow) and an Ask that failed. Everything else is
 *  live work, which is stopped from its own modal rather than hidden. */
export function activityDismissable(
  activity: Pick<RunnerActivity, 'kind' | 'state'>
): boolean {
  return (
    activity.kind === 'signin' ||
    (activity.kind === 'ask' && activity.state === 'error')
  );
}

// ── IPC action map (mirrors desktop/src/types.ts) ──

/**
 * How the host that stores this space's credentials is doing at protecting
 * them. There is no unencrypted mode: a host that cannot encrypt reports
 * `ready: false` and refuses to read or write secrets at all, carrying the
 * problem and its remedy so the UI can print them verbatim.
 */
export type SecureStoreStatus = {
  backend: 'runner-keychain' | 'electron-safe-storage';
  /** Which OS facility holds the machine key. Informational, drives UI copy. */
  keyStore: 'macos-keychain' | 'libsecret' | 'dpapi' | 'electron-safe-storage';
  /** Reads and writes of credentials will succeed right now. */
  ready: boolean;
  /** Whether the client should show a password box. No host offers a password
   *  to set any more; a runner reports true only while connection files from
   *  before the password was removed are still locked behind it, which is when
   *  an unlock box is needed. Runners older than that report true always. */
  passwordSupported: boolean;
  /** Whether a connection password from an older runner still exists. It is
   *  removed by the unlock, or at start when nothing needs it. */
  passwordEnabled: boolean;
  /** Whether the key is available in the host process right now. */
  unlocked: boolean;
  /** Missing binary, when that's the cause. Empty otherwise. */
  missingTool: string;
  /** One-line problem statement. Empty when ready. */
  problem: string;
  /** Copy-pasteable remedy, possibly multi-line. Empty when ready. */
  fix: string;
};

export type ConfigResponse = {
  repoUrl: string;
  mainBranch: string;
  gitName: string;
  gitEmail: string;
  // The CLI agent this space runs. Older runners omit this (claude).
  provider: AgentProvider;
  // True when the runner's Claude CLI is signed in - the only Claude auth.
  // One sign-in per runner, in its own CLI home (agent-homes.ts), shared by
  // every board and by Oakbox. Older runners omit this.
  claudeLoggedIn: boolean;
  // True when the runner's Codex CLI is signed in (auth.json in its CLI
  // home), likewise per runner. Older runners omit this.
  codexLoggedIn: boolean;
  hasGithubToken: boolean;
  githubTokenHint: string;
  // The credential itself is never surfaced - only whether it's connected.
  onedrive: { connected: boolean };
  // Whether this host can encrypt connection tokens at rest right now. False
  // means it reported back that it CANNOT - a machine with no usable keychain,
  // or one still holding connection files locked behind an old connection
  // password (unlocked once, then never again). Such a runner stores nothing
  // rather than falling back to plaintext.
  //
  // Undefined is a THIRD state, not a synonym for false: a runner too old to
  // know about this field omits it. Such a runner is certainly not encrypting,
  // but the reason is "old build", not "no keychain" - and the UI must not
  // assert a cause it can't observe. Keep the distinction; don't `?? false`.
  tokensEncrypted?: boolean;
  // The detail behind tokensEncrypted: which key store, whether an old
  // connection password still needs entering, and the exact fix when the
  // store is not usable. Omitted by runners that predate the secure-store
  // rework.
  secureStore?: SecureStoreStatus;
};

/** A folder the owner picked to index. The selection is the whole index scope:
 *  the host never walks anything outside it. */
export type OneDriveSelectedFolder = {
  itemId: string;
  name: string;
  /** Full OneDrive path of the folder ('/Documents/Projects'). */
  filePath: string;
};

/** One row in the folder picker. `covered` means a selected ancestor already
 *  indexes this folder, so it offers no Sync action of its own. */
export type OneDriveBrowsedFolder = {
  id: string;
  name: string;
  filePath: string;
  childCount: number;
  selected: boolean;
  covered: boolean;
  /** Path of the selected ancestor, when covered. */
  coveredBy?: string;
};

export type SetConfigPayload = {
  repoUrl?: string;
  mainBranch?: string;
  gitName?: string;
  gitEmail?: string;
  githubToken?: string;
  provider?: AgentProvider;
};

/** Result of one device-code poll. */
export type OneDrivePollStatus =
  | 'pending'
  | 'slow_down'
  | 'complete'
  | 'expired'
  | 'declined'
  | 'error';

/** One path in a bare workspace's git status, tagged with the porcelain letter
 *  that put it there: 'M' modified, 'A' added, 'D' deleted, 'R' renamed, 'U'
 *  unmerged. The same path can appear staged AND unstaged - git tracks the
 *  index and the working tree separately, and so does the panel. */
export type BareGitFileStatus = {
  path: string;
  code: string;
};

/** One commit on the workspace's branch, for the panel's history list. */
export type BareGitCommit = {
  sha: string;
  shortSha: string;
  author: string;
  /** ISO-8601 author date. */
  date: string;
  /** Git's own phrasing ("3 hours ago"), so the panel needs no clock. */
  relative: string;
  subject: string;
  /** Not reachable from any remote branch: committed here, never pushed. */
  local: boolean;
};

/** What the scratchpad panel does with one file. Derived from the extension
 *  alone (`helpers/scratchpad.ts`), so an agent picks a renderer by naming the
 *  file rather than by writing a manifest beside it. */
export type ScratchpadKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'html'
  | 'markdown'
  | 'link'
  | 'text';

/** One file on a space's scratchpad. Contents are fetched per item, so a panel
 *  listing a 40MB video costs one small call until something asks to play it. */
export type ScratchpadItem = {
  /** Bare filename. The scratchpad is flat: no separators, ever. */
  name: string;
  kind: ScratchpadKind;
  size: number;
  /** Epoch ms. The panel orders newest first. */
  modifiedAt: number;
  /** Past SCRATCHPAD_MAX_BYTES: listed, never inlined. The panel says so
   *  rather than hanging on a read the transport would take minutes to chunk. */
  tooLarge: boolean;
};

/** One vault backup a runner holds for its owner. No kind and no path: there
 *  is one kind, and the path is the runner's business. See
 *  .specs/runner-vault-backup.md. */
export type RunnerVaultBackupItem = {
  /** Picked by the runner from its own clock; a client never names a file. */
  name: string;
  size: number;
  /** Epoch ms, from the file. The order comes from the name, not from this. */
  modifiedAt: number;
};

/** One file in a runner's Vault (.specs/desktop-ai.md "The Vault"): its
 *  Upload row, with the type and size its object has. */
export type RunnerVaultFile = {
  /** Upload.id */
  id: string;
  /** The path, folders and all, as the cloud vault keeps it. */
  name: string;
  size: number;
  contentType: string;
  /** ISO time it was added. */
  createdAt: string;
  ingestStatus: 'PENDING' | 'EMBEDDED' | 'FAILED';
  /** The caller added it: only they may delete it. */
  mine: boolean;
  /** Who added it, when it was not the caller (server user id). */
  addedBy: string | null;
  /** The categories (factiii ids) the runner's Categorize tagged it with.
   *  Absent from a runner from before Categorize. */
  factiiiIds?: number[];
  /** A note kept from Ask (.specs/desktop-ai.md "Kept research on the
   *  runner"), not a file: `id` is its Post.uuid and `name` its title. It
   *  opens with `vaultNotes`, never as a download. */
  note?: true;
};

/** One file a runner's Vault found for a search (.specs/desktop-ai.md "The
 *  Vault", "Search across vaults"). `distance` is bge-base's cosine distance
 *  (`<=>`), the cloud's model and size, so it ranks beside the cloud's own
 *  hits; null for a file whose name or text has the words. */
export type RunnerVaultHit = RunnerVaultFile & { distance: number | null };

/** How a runner's Vault answered a search. `words`: the name or the text has
 *  them, as the cloud's file search. `meaning`: no such file, so the query was
 *  embedded on the runner and ranked by distance. `names`: Vault search is not
 *  installed (or its embedder did not answer), so only the words pass ran.
 *  `absent`: this runner has no Vault. */
export type RunnerVaultSearchMode = 'words' | 'meaning' | 'names' | 'absent';

/** What Oakbox Ask may run on a runner (.specs/desktop-ai.md "Ask with a
 *  runner's model"). Claude and Codex run on the owner's sign-in. */
export type RunnerAskEngine = 'ollama' | 'claude' | 'codex';

/** The models a runner offers the caller for Ask. */
export type RunnerAskModels = {
  /** The Ollama models on the runner: `id` is Ollama's tag, `title` the
   *  Local AI table's name for it, else the tag. */
  ollama: { id: string; title: string }[];
  /** Claude, then Codex, where the runner has the CLI. Always empty for a
   *  shared-vault peer. */
  cli: Exclude<RunnerAskEngine, 'ollama'>[];
  /** Of `cli`, the ones not signed in, so Ask's menu says so. Absent from an
   *  older runner: every CLI it lists reads as signed in. */
  signedOut?: Exclude<RunnerAskEngine, 'ollama'>[];
};

/** One turn of a runner Ask, as the app last read it. `text` grows while the
 *  model writes; `sources` are the files it was given to answer from. */
export type RunnerAskState = {
  steps: string[];
  text: string;
  done: boolean;
  error: string | null;
  sources: RunnerVaultFile[];
  /** The note the model kept this turn with its save tool, if any. Absent
   *  from a runner from before the tool. */
  saved?: RunnerVaultNote;
  /** The asker stopped it (`vaultAskCancel`): done, and no answer was
   *  saved. Absent from a runner from before the stop. */
  stopped?: boolean;
};

/** An Ask chat kept on a runner (.specs/desktop-ai.md "Runner chats"). It
 *  stays on the model it started on. */
export type RunnerAskChat = {
  id: string;
  /** The first question. */
  title: string;
  engine: RunnerAskEngine;
  /** The Ollama tag; null for Claude and Codex. */
  model: string | null;
  /** The model's name, for the prompt bar. */
  modelTitle: string;
  updatedAt: string;
  /** Why the last turn failed; null once one succeeds. */
  error: string | null;
  /** The last turn was stopped before it answered; false once one answers.
   *  Absent from a runner from before the stop. */
  stopped?: boolean;
};

/** One message of a runner chat: a question, or an answer with what it did
 *  on the way, how long it took and the files it answered from. */
export type RunnerAskChatMessage = {
  role: 'you' | 'answer';
  text: string;
  steps: string[];
  ms: number | null;
  sources: RunnerVaultFile[];
};

/** The Vault's reading and embedding of its files, on the runner. */
export type RunnerVaultIngest = {
  /** Files waiting to be read, read and searchable, or unreadable. */
  pending: number;
  embedded: number;
  failed: number;
  running: boolean;
  /** The path of the file being read now. */
  current: string | null;
  /** `unsupported`: neither a Mac with Apple silicon (Core ML) nor Linux
   *  (ONNX). */
  embedder: 'ready' | 'missing' | 'unsupported';
};

export type RunnerVaultStatus = {
  /** The caller is the runner's owner, or a member the owner let in. */
  role: 'owner' | 'member';
  files: number;
  bytes: number;
  /** Owner only: the accounts the owner let into this vault. */
  members: string[];
  /** Absent from a runner from before the embedder. */
  ingest?: RunnerVaultIngest;
};

/** A runner's Categorize run (.specs/desktop-ai.md "Categorize on the
 *  runner"), as the app last read it. */
export type RunnerCategorizeRun = {
  engine: RunnerAskEngine;
  /** The Ollama tag; null for Claude and Codex. */
  model: string | null;
  running: boolean;
  /** Files in the run, and how many are done. */
  total: number;
  processed: number;
  /** The path of the file being read now. */
  current: string | null;
  /** Seconds left, from the files done so far; null before the first. */
  etaSeconds: number | null;
  stopped: boolean;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
};

/** One value a category holds on a runner file (PostFactiii), in the shape
 *  `tagDetails` reads: a number, text, and the dates and places written. */
export type RunnerTagEntry = {
  data: string | null;
  number: number | null;
  spaceTimes: { timestamp: string | null; display: string | null }[];
};

/** A runner file's own page (.specs/desktop-ai.md "A runner file's page"):
 *  the file, what the runner read off it and the categories its Categorize
 *  gave it, with their values. */
export type RunnerVaultFilePost = {
  file: RunnerVaultFile;
  /** Why reading it failed, when it did. */
  ingestError: string | null;
  /** How its text was read (TEXT_LAYER, OCR, …); null until read. */
  transcriptSource: string | null;
  /** When Categorize last ran over it; null for never. */
  categorizedAt: string | null;
  /** The text read off it, its pages marked, cut at `textLimit`. Empty
   *  until the runner has read it. */
  text: string;
  /** The whole text's length; over `text`'s when it was cut. */
  textChars: number;
  /** Its searchable pieces (content rows). */
  pieces: number;
  /** Its categories by factiii id, each with the values it holds. */
  tags: { factiiiId: number; entries: RunnerTagEntry[] }[];
};

/** A note kept from a runner Ask answer (.specs/desktop-ai.md "Kept research
 *  on the runner"): a PRIVATE post with no file, in the runner's Vault. */
export type RunnerVaultNote = {
  /** Post.uuid */
  id: string;
  title: string;
  /** The answer's HTML, as it was kept. */
  body: string;
  /** ISO time it was kept. */
  at: string;
};

/** Categorize on a runner: what waits, how many files each category tagged,
 *  the models it may run on and the run, if any. */
export type RunnerCategorizeStatus = {
  /** Read files no run has categorized yet. */
  waiting: number;
  /** Files the runner has not read yet (its ingest): Categorize waits on
   *  them. */
  unread: number;
  /** Files per category (factiii id). */
  counts: { factiiiId: number; files: number }[];
  models: RunnerAskModels;
  run: RunnerCategorizeRun | null;
};

/** A running simulator. Only booted devices are listed — a shutdown one has no
 *  display to attach to. See .specs/simulator-panel.md. */
export type SimulatorDevice = {
  udid: string;
  name: string;
  /** Trimmed for display: "iOS 26 4", not the full reverse-DNS runtime id. */
  runtime: string;
};

/**
 * One input event for a simulator.
 *
 * Touch carries 0..1 ratios, not points: the HID layer normalises against the
 * screen itself, so neither end needs to know the device's size. `move` is what
 * separates this from a canned swipe — the simulator tracks the pointer as it
 * travels, which is the difference between scrolling and watching a list jump
 * once on release.
 */
export type SimulatorCommand =
  | { type: 'touch'; phase: 'down' | 'move' | 'up'; x: number; y: number }
  | { type: 'tap'; x: number; y: number }
  | { type: 'text'; text: string }
  /** One key going down or up, as a USB HID usage code. What a hardware
   *  keyboard sends: the device supplies shift, repeat and shortcuts itself. */
  | { type: 'key'; code: number; down: boolean }
  | { type: 'button'; button: 'HOME' | 'LOCK' | 'SIRI' | 'SIDE' | 'APPLE_PAY' }
  /** Face ID / Touch ID. Not HID — a Darwin notification, the same one
   *  Simulator.app's Features menu posts. A non-match is the more useful test:
   *  it is the path an app's error handling actually has to cover. */
  | { type: 'biometric'; kind: 'face' | 'touch'; match: boolean };

/** A command plus the device it is for. Split from `SimulatorCommand` because
 *  `Omit` over a union collapses it, and the panel builds commands without
 *  knowing the udid. */
export type SimulatorInput = SimulatorCommand & { udid: string };

/** What the owner gives one board member on a runner, lowest first. Each
 *  level includes the ones before it (runner-access.ts). */
export const RUNNER_ACCESS_LEVELS = [
  'none',
  'api',
  'terminals',
  'screens'
] as const;
export type RunnerAccessLevel = (typeof RUNNER_ACCESS_LEVELS)[number];

/** Every level the owner gave: board slug -> user id -> level. A board or a
 *  user that is not listed is at 'none'. Lives on the runner (access.json). */
export type RunnerGrants = Record<string, Record<string, RunnerAccessLevel>>;

/** Who the owner let run dangerous jobs without
 *  asking: board slug -> the user ids. Kept beside the levels in access.json,
 *  and counts only for a member who also has the API level there. Anyone
 *  else's call waits for the owner's approval, which is what every file from
 *  before it reads as. */
export type RunnerDangerousGrants = Record<string, string[]>;

/** One board's access as the runner tells it to the asking channel. */
export type RunnerAccessState = {
  /** The asker's own level on this board. The owner reads 'screens'. */
  level: RunnerAccessLevel;
  /** User id -> level on this board. Sent to the owner only; a member gets
   *  an empty map and never learns another member's level. */
  grants: Record<string, RunnerAccessLevel>;
  /** The user ids with the permission on this board. Sent to the owner only,
   *  like `grants`. Absent from a runner that predates it, which the owner's
   *  app reads as "update the runner to set it". */
  dangerousGrants?: string[];
};

/** Which part of the product a tool of the environment is for. */
export type EnvironmentFeature = 'boards' | 'terminals' | 'card-ai' | 'secrets';

/** One tool the runner's environment needs, as `environmentStatus` reports
 *  it. No path of the machine: the name, the state and the install command
 *  are what a member may see. */
export type EnvironmentItem = {
  id: string;
  title: string;
  feature: EnvironmentFeature;
  required: boolean;
  state: 'ok' | 'missing';
  /** Who installs a missing item on this host. Null when it is present. */
  fix: EnvironmentFix | null;
};

/** `initialize`: the Initialize button installs it; `password` when it will
 *  ask for the computer's password first (sudo is not passwordless). `hand`:
 *  Initialize does not, so a person does, with the command when the runner
 *  knows one. */
export type EnvironmentFix =
  | { by: 'initialize'; password: boolean }
  | { by: 'hand'; command?: string };

/** Who has the runner's one video stream (the simulator or the desktop
 *  view), as the runner tells one channel. `mine` is true on the holder's own
 *  link only. See screen-holder.ts. */
export type ScreenHolderView = {
  kind: 'simulator' | 'screen';
  userId: string;
  name: string;
  mine: boolean;
};

/** The image formats an AI question can carry. A GIF is sent as a still. */
export const ASK_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif'
] as const;
export type AskImageType = (typeof ASK_IMAGE_TYPES)[number];

/** An image attached to an Ask Board question: base64 in the request itself.
 *  It is context for one turn, never an upload - nothing reaches S3/R2, and
 *  the runner keeps no copy once the turn ends. */
export type AskImage = { mediaType: AskImageType; data: string };

export const ASK_IMAGE_MAX_COUNT = 4;
/** Per image, decoded. Clients downscale well below this before sending. */
export const ASK_IMAGE_MAX_BYTES = 5_000_000;

/** The largest file a terminal takes as a drop or a paste, decoded. One
 *  number for the client (which refuses before it uploads) and the runner
 *  (which refuses again, on disk, across the chunks). */
export const TERMINAL_DROP_MAX_BYTES = 100_000_000;

/** Shown by the clients when `githubDisconnect` answers `revoked: false`: the
 *  token is gone from the runner but may still be valid at GitHub. */
export const GITHUB_NOT_REVOKED_MESSAGE =
  'Removed here, but not revoked at GitHub. Revoke it at github.com/settings/applications.';

/** Where the owner removes Factiii's grant itself. Disconnecting deletes the
 *  connection and the Graph subscription, not the consent. */
export const ONEDRIVE_CONSENT_URL = 'https://account.live.com/consent/Manage';

/** Shown after every OneDrive disconnect, the URL as a link. */
export const ONEDRIVE_REVOKE_HINT =
  "To remove Factiii's access completely, go to";

/** Shown when `onedriveDisconnect` answers `subscriptionRemoved: false`. */
export const ONEDRIVE_UNREACHABLE_MESSAGE =
  'Removed here, but Microsoft could not be reached.';

/** Where to revoke the connections a runner holds (a revoked runner keeps
 *  them until they are revoked at the provider). */
export const GITHUB_APPLICATIONS_URL =
  'https://github.com/settings/applications';

/** One turn of the Ask Board conversation as the runner keeps it. */
export type BoardSessionMessage = {
  role: 'user' | 'assistant';
  content: string;
  /** How many images rode with a user turn. The images are not kept. */
  imageCount?: number;
};

/** One Board AI conversation in the session list. */
export type BoardConversationSummary = {
  id: string;
  /** The first question, until the user renames it. */
  title: string;
  /** "List > Card title" when the conversation is about one card, else ''. */
  topic: string;
  /** The last thing the user asked. */
  preview: string;
  provider: AgentProvider | null;
  /** Set aside: out of the open tabs, still resumable from the history. */
  archived: boolean;
  /** A turn is in flight on the runner right now. */
  busy: boolean;
  messageCount: number;
  createdAt: number;
  updatedAt: number;
};

export type BoardAgentActionMap = {
  getConfig: { payload: void; result: ConfigResponse };
  setConfig: { payload: SetConfigPayload; result: { success: boolean } };
  clearToken: {
    payload: { key: 'githubToken' };
    result: { success: boolean };
  };
  ask: {
    payload: {
      question: string;
      boardContext?: string;
      componentSchema?: string;
      /** Provider for a NEW conversation. Locked once the conversation starts
       *  (a claude session and a codex thread aren't interchangeable). */
      provider?: AgentProvider;
      /** Screenshots and images for this turn, piped straight to the model. */
      images?: AskImage[];
      /** Which conversation this turn belongs to; the first question with a
       *  new id starts it. A client older than conversations sends none and
       *  gets the most recently used one. */
      conversationId?: string;
    };
    result: string;
  };
  /** From a client older than conversations: set the current one aside. It
   *  stays in the history; nothing is deleted. */
  resetBoardSession: { payload: void; result: void };
  /** One persisted Ask Board conversation, for hydrating the panel on open. No
   *  id means the most recently used one. */
  boardSession: {
    payload: { conversationId?: string } | void;
    result: {
      messages: BoardSessionMessage[];
      /** Absent from a runner older than conversations. */
      conversationId?: string;
      busy?: boolean;
    };
  };
  /** Every Ask Board conversation of the space, most recently used first. */
  boardSessions: {
    payload: void;
    result: { conversations: BoardConversationSummary[] };
  };
  /** Rename, label with a topic, or set aside / bring back a conversation. */
  boardSessionUpdate: {
    payload: {
      conversationId: string;
      title?: string;
      topic?: string;
      archived?: boolean;
    };
    result: void;
  };
  /** Delete a conversation for good, stopping any turn in flight. */
  boardSessionDelete: { payload: { conversationId: string }; result: void };
  /** Spotlight-style OneDrive index status + persistent size for the UI. */
  onedriveIndexStatus: {
    payload: void;
    result: {
      status: 'indexing' | 'idle' | 'error';
      done: number;
      error?: string;
      files: number;
      chunks: number;
      images: number;
      /** How many folders the owner picked to index. */
      folders: number;
      selected: OneDriveSelectedFolder[];
    };
  };
  /** Browse one level of the drive for the folder picker. No `parentId` browses
   *  the drive root. */
  onedriveBrowseFolders: {
    payload: { parentId?: string };
    result: { folders: OneDriveBrowsedFolder[]; fileCount: number };
  };
  /** Add a folder to the index selection; the host indexes it right away and
   *  keeps it in sync from then on. */
  onedriveSelectFolder: {
    payload: OneDriveSelectedFolder;
    result: { success: boolean };
  };
  /** Remove a folder from the selection, deleting everything indexed under it. */
  onedriveUnselectFolder: {
    payload: { itemId: string };
    result: { success: boolean };
  };
  /** Semantic search over the runner's OneDrive index (docs + images). */
  onedriveSearch: {
    payload: { query: string; k?: number };
    result: {
      itemId: string;
      name: string;
      filePath: string;
      kind: 'text' | 'image';
      seq?: number;
      distance: number;
    }[];
  };
  cardExecute: {
    payload: {
      postId: string;
      prompt: string;
      baseBranch: string;
      targetBranch: string;
      taskTitle: string;
      effort: string;
      mode: CardAIMode;
      // Per-job agent provider, defaulting to the space config when absent.
      provider?: AgentProvider;
    };
    result: { taskId: string; postId: string };
  };
  // On-demand live dev-server preview for a completed claude session.
  cardStartPreview: {
    payload: { postId: string };
    result: { previewUrl: string };
  };
  // One-shot on the runner host: combine the selected cards' text into the
  // merged card's title/content. Runs before posts.merge creates the card.
  cardMergeContent: {
    payload: { cards: { title: string; content: string }[] };
    result: { title: string; content: string };
  };
  // Card merge follow-up: take the diffs from the source cards' completed
  // sessions and have Claude reconcile them in a fresh session on the primary
  // card, ending in a normal 'complete' phase (diff review + push).
  cardMergeSessions: {
    payload: {
      primaryPostId: string;
      // Cards whose completed sessions supply the diffs; may include the primary.
      sourcePostIds: string[];
      taskTitle: string;
    };
    result: { taskId: string; postId: string };
  };
  cardSessions: { payload: void; result: Record<string, CardAISessionSummary> };
  cardSession: {
    payload: { postId: string };
    result: CardAISessionFull | null;
  };
  cardDismiss: { payload: { postId: string }; result: void };
  setupStatus: {
    payload: void;
    result: { phase: 'idle' | 'running' | 'ready' };
  };
  /** What the environment has and lacks, tool by tool. Read-only. */
  environmentStatus: { payload: void; result: EnvironmentItem[] };
  ensureSetup: { payload: void; result: { ready: boolean } };
  cardRemoteBranches: { payload: void; result: string[] };
  /** Worktree names already in use, so the terminal launcher can refuse a
   *  duplicate name up front instead of failing the launch. */
  bareWorkNames: { payload: void; result: string[] };
  // Deploy button entry: does the default branch carry the deploy contract
  // (.agents/skills/deploy/SKILL.md + required-variables.json)? Missing
  // pieces route the UI to the skill creator. See .specs/deploy.md.
  deployStatus: {
    payload: void;
    result: {
      hasSkill: boolean;
      hasVariables: boolean;
      hasChecker: boolean;
      branch: string;
      // Newest unmerged deploy-skill* branch with the full contract; the
      // creator offers to resume it instead of regenerating.
      pendingBranch: string;
      variables: {
        key: string;
        title: string;
        description: string;
        category: string;
        sensitive: boolean;
      }[];
    };
  };
  // The Deploy modal's release picker: what is on origin that could ship,
  // newest commit first. The default branch is excluded - it IS production,
  // so a release never starts there. `ahead` = commits the branch would bring
  // to it (0 = already merged), `updated` = its last commit, epoch SECONDS.
  deployBranches: {
    payload: void;
    result: { name: string; ahead: number; updated: number; subject: string }[];
  };
  // Runner secrets store (the AI-safe contract: values are write-only,
  // the wire carries key NAMES and set-status only). NO caching: every
  // operation takes the password and decrypts for that one call.
  deploySecretsStatus: {
    payload: void;
    result: { initialized: boolean };
  };
  deploySecretsInit: {
    payload: { password: string };
    result: { success: boolean };
  };
  // Password check + key names in one call (the settings view's entry).
  // `meta` is the descriptions sidecar (secrets.meta.json): what each key is
  // FOR, never what it is. It rides this call rather than one of its own
  // because the panel wants both at once, and it needs no password of its own.
  deploySecretsList: {
    payload: { password: string };
    result: {
      keys: string[];
      meta: Record<string, SecretMeta>;
      /** File-secret (sops blob) NAMES. Older runners omit it. */
      files?: string[];
      /** Keys the runner supplies itself when the store has no value: GH_TOKEN
       *  and GITHUB_TOKEN, from its GitHub connection. Older runners omit it. */
      provided?: string[];
    };
  };
  deploySecretsSet: {
    payload: { password: string; key: string; value: string };
    result: { success: boolean };
  };
  // Describe a key. No password: this writes no secret material, and sops
  // already leaves key names in cleartext. Omitted fields keep their current
  // text, so saving a title cannot blank a description.
  deploySecretsSetMeta: {
    payload: { key: string } & Partial<SecretMeta>;
    result: { success: boolean };
  };
  deploySecretsUnset: {
    payload: { password: string; key: string };
    result: { success: boolean };
  };
  /** Store a file secret (sops blob at secrets/files/<NAME>.enc), replacing
   *  any with that name. Owner only. `name` is env-style (it becomes the
   *  variable holding the temp file's path); the file is at most 5 MB. */
  deploySecretsSetFile: {
    payload: { password: string; name: string; contentBase64: string };
    result: { success: boolean };
  };
  /** Remove a file secret. Owner only. */
  deploySecretsUnsetFile: {
    payload: { password: string; name: string };
    result: { success: boolean };
  };
  /** The keys inside a file secret that is an env file (a server env such as
   *  PROD_SERVER_ENV): names and masked tails, never a value. `env: false`
   *  for a file that is not one (a `.pem`, a JSON key). Owner only. A runner
   *  that predates this answers "Unknown board-agent action". */
  secretFileEnvKeys: {
    payload: { password: string; name: string };
    result: { env: false } | { env: true; keys: SecretFileEnvKey[] };
  };
  /** Set one key inside an env file secret, in place: the runner changes
   *  that line and re-encrypts the file. A key the file lacks is appended.
   *  `occurrences` over 1 means the key is written more than once and its
   *  last line, the one a reader uses, was changed. Owner only. */
  secretFileEnvSet: {
    payload: { password: string; name: string; key: string; value: string };
    result: { success: boolean; occurrences: number; keys: SecretFileEnvKey[] };
  };
  /** Remove one key from an env file secret: every line that sets it
   *  (`removed`). Owner only. */
  secretFileEnvUnset: {
    payload: { password: string; name: string; key: string };
    result: { success: boolean; removed: number; keys: SecretFileEnvKey[] };
  };
  // Skill creator: the AI analyzes the repo + the owner's described process
  // and drafts SKILL.md + required-variables.json into the host workspace,
  // UNCOMMITTED. The owner previews the skill and its requested secrets,
  // revises, then explicitly approves the push. Long-running; streams via the
  // board log.
  deployCreateSkill: {
    // `provider` is per-run, defaulting to the space config when absent - the
    // same shape cardExecute uses. Every deploy action that spawns an agent
    // takes it, so one pick in the modal covers check/fix, create and run.
    payload: { instructions: string; provider?: AgentProvider };
    result: {
      skill: string;
      // The generated check-environment.sh, for the review UI.
      checker: string;
      variables: {
        key: string;
        title: string;
        description: string;
        // AI-chosen grouping: server, client, database, deploy access...
        category: string;
        sensitive: boolean;
      }[];
    };
  };
  // ── Agent skills (SKILL.md), space-scoped like the deploy family ──
  // `.agents/skills` is the only source of truth; Claude Code reaches it
  // through a `.claude/skills/<name>` symlink, which `claudeLink` tracks.
  // Two scopes, both served by host-skills.ts:
  //   global - the host's own ~/.agents/skills. Written directly; every space
  //            this runner drives sees it.
  //   repo   - committed skills, read at a ref. Edits become a pushed
  //            `skill/*` branch and stay pending until a human merges it.
  skillsList: {
    payload: void;
    result: {
      global: SkillSummary[];
      repo: SkillSummary[];
      /** Whether `.claude/skills` links to `.agents/skills` in each scope.
       *  Anything but `linked` blocks the skills manager until the user
       *  migrates. */
      globalLink: SkillLinkState;
      repoLink: SkillLinkState;
      repoBranch: string;
      // False when the space has no repo configured, so the UI explains the
      // gap rather than showing an empty list that reads as "no skills".
      repoAvailable: boolean;
      // `skill/migrate` while a migration is pushed and unmerged, else ''. The
      // gate stays up until the merge lands, so the UI needs this to explain
      // the wait instead of offering to propose the same branch again.
      repoMigrationBranch: string;
      // Skills that landed on the default branch after that migration was
      // pushed, so its branch no longer moves all of them. Normally empty.
      repoMigrationMissing: string[];
    };
  };
  skillRead: {
    payload: { scope: SkillScope; name: string };
    result: {
      name: string;
      scope: SkillScope;
      content: string;
      errors: string[];
      // Other files in the skill dir. Listed for context; not editable in v1.
      bundleFiles: string[];
      status: SkillStatus;
      pendingBranch: string;
    };
  };
  skillWriteGlobal: {
    payload: { name: string; content: string };
    result: void;
  };
  skillDeleteGlobal: {
    payload: { name: string };
    result: void;
  };
  /** Push the edit to `skill/<name>` and stop. Merging on GitHub is the
   *  approval gate, as with approveDeploySkill and cardFinalize. */
  skillProposeRepo: {
    payload: { name: string; content: string };
    result: { branch: string };
  };
  /** Move skills out of `.claude/skills` and `.codex/skills` into
   *  `.agents/skills`, then replace `.claude/skills` with one symlink. Global
   *  applies at once; repo returns the review branch to merge. */
  skillMigrate: {
    payload: { scope: SkillScope };
    // `pushed` is false when the branch was already open and unchanged, so the
    // UI can say "already waiting" instead of claiming a fresh push.
    result: { branch: string; moved: string[]; pushed: boolean };
  };

  // ── Deploy run (executing the merged skill on the host) ──
  // Start returns immediately; progress rides the 'deploy-run' event +
  // deployRunState. Gates: answers resume the agent session; authorization
  // carries the store password RUNNER-DIRECT (never through the session).
  deployRunStart: {
    payload: {
      provider?: AgentProvider;
      /** Release branch picked in the modal. Omitted (older clients, or no
       *  pick) leaves the skill to ask for it during the run. */
      branch?: string;
    };
    result: { started: boolean };
  };
  deployRunState: {
    payload: void;
    result: {
      /** The CLI this run is actually using. Reported rather than inferred:
       *  a reopened modal would otherwise show the space default while the
       *  run is on the other one. Older runners omit it. */
      provider: AgentProvider;
      phase:
        | 'idle'
        | 'running'
        | 'awaiting-input'
        | 'awaiting-auth'
        | 'success'
        | 'failed';
      todos: CardAITodo[];
      logs: CardAILogEntry[];
      question: string;
      pendingCommand: string;
      /** The keys the pending command will receive - the scope the owner is
       *  actually authorizing. Older runners omit it. */
      pendingKeys: string[];
      error: string;
      // Agent-written end-of-run report (text after DEPLOY_COMPLETE/
      // DEPLOY_FAILED): what shipped, what was skipped, owner follow-ups.
      summary: string;
      startedAt: number;
      finishedAt: number;
    };
  };
  deployRunAnswer: {
    payload: { answer: string };
    result: { success: boolean };
  };
  /** Answer a sudo request from Initialize. An empty password denies, and the
   *  install fails with a message instead of hanging. Runner-direct: the
   *  password never reaches the server. */
  setupAuthorize: {
    payload: { password: string };
    result: { success: boolean };
  };
  /** Kept for clients older than the global authorization modal: both answer
   *  whatever the space's gate is currently waiting on. New clients send
   *  `secretsAuthAnswer`, which names the request it is answering. */
  deployRunAuthorize: {
    payload: { password: string };
    result: { success: boolean };
  };
  deployRunDeny: { payload: void; result: { success: boolean } };
  /** What the global authorization modal renders: the one command waiting on
   *  the owner's store password, wherever in the space it was run from.
   *  `pending: null` means nothing is waiting - the modal closes. */
  secretsAuthState: {
    payload: void;
    result: {
      pending: PendingSecretsAuth | null;
      /** The open unlock window (jobs only), or null when locked. */
      unlocked: SecretsUnlockWindow | null;
    };
  };
  /** Answer a specific pending request. Omitting `password` denies it. The id
   *  must match the request on screen: a wrong-password re-prompt replaces it,
   *  and a stale answer must not resolve its successor. Runner-direct - the
   *  password never reaches the server. */
  secretsAuthAnswer: {
    /** `unlockFor` (minutes, default 120, max 720) opens an unlock window
     *  when the request is a job: later job spawns run without a prompt until
     *  it ends. 0 or absent on a free command = this one spawn only. */
    payload: { id: string; password?: string; unlockFor?: number };
    result: { success: boolean };
  };
  /** The members' dangerous job calls waiting on the runner owner (owner
   *  only). Empty when nothing waits. */
  dangerousApprovalState: {
    payload: void;
    result: { pending: PendingDangerousApproval[] };
  };
  /** The runner owner's answer to one waiting call: approve runs that one
   *  call only; deny ends it as denied. No password: the runner checks the
   *  actor is its owner. False when the id is no longer waiting. */
  dangerousApprovalAnswer: {
    payload: { id: string; approve: boolean };
    result: { success: boolean };
  };
  /** End the unlock window now: the runner wipes the age key from memory. */
  secretsLock: {
    payload: Record<string, never>;
    result: { success: boolean };
  };
  /** Re-wrap the age key under a new password (owner only). No secret value
   *  changes; the old password stops working. */
  secretsChangePassword: {
    payload: { oldPassword: string; newPassword: string };
    result: { success: boolean };
  };
  /** An offline backup of this board's secrets (owner only, never over the
   *  relay): the runner reads the keys, their descriptions and the file
   *  secrets out of the store with `password`, the board password, and seals
   *  them under `passphrase`. The passphrase alone opens the file. */
  secretsExportBackup: {
    payload: { password: string; passphrase: string };
    result: { filename: string; bundle: string };
  };
  /** Read a backup file's secrets (owner only, never over the relay). The
   *  runner unseals `bundle` with `passphrase` and answers with the values.
   *  The one action whose result carries secret values; no board on the
   *  runner is read or changed by it. A backup of the older format, which
   *  also needed a board password, is refused. */
  secretsOpenBackup: {
    payload: { bundle: string; passphrase: string };
    result: OpenedSecretsBackup;
  };
  /** What importing a backup file into THIS board would do (owner only, never
   *  over the relay): each of the backup's secrets beside the board's own, as
   *  masked tails. Nothing is written. `targetPassword` is this board's
   *  password, which reading its store takes; a board with no store has
   *  nothing to read and every `current` is null. A runner that predates this
   *  answers "Unknown board-agent action". */
  secretsImportBackupPlan: {
    payload: SecretsBackupImportRequest;
    result: SecretsBackupImportPlan;
  };
  /** Import the picked secrets of a backup file into THIS board. The runner
   *  opens the backup again, re-checks every pick against the store as it is
   *  now, and encrypts each value to this board's key; no value crosses the
   *  link. A pick the board already holds with another value needs
   *  `conflict`, or the whole import is refused. `targetPassword` is this
   *  board's password, or the password for its new store when it has none. */
  secretsImportBackupApply: {
    payload: SecretsBackupImportRequest & {
      secrets: BackupImportPick[];
      files: BackupImportPick[];
    };
    /** The names written. A pick left out was kept or already equal. */
    result: { secrets: string[]; files: string[] };
  };
  /** What this runner gives the members of this board (runner-access.ts):
   *  one level per member, None, API, Terminals or Screens. Any channel may
   *  read its own level (a member's app hides what it cannot use); only the
   *  owner sets one, and a set moves one member on this board. */
  runnerAccessGet: {
    payload: void;
    result: RunnerAccessState;
  };
  runnerAccessSet: {
    /** One change per call: a member's level, or whether they run
     *  dangerous jobs without the owner's approval per call. */
    payload:
      | { userId: string; level: RunnerAccessLevel }
      | { userId: string; dangerous: boolean };
    result: RunnerAccessState;
  };
  /** What needs the owner on this runner and board (the attention badge).
   *  Owner only. Names and booleans, never a value, and cheap: read from the
   *  clone as last fetched and the store's cleartext key names. */
  attention: { payload: void; result: RunnerAttention };
  /** The runner's always-on service (start at login, restart after a crash).
   *  Owner only, runner-wide: one service per runner, whichever board asks. */
  serviceStatus: { payload: void; result: RunnerServiceState };
  /** Write and load the service. Never starts a second runner beside this
   *  one: the service then starts at the next login. */
  serviceInstall: { payload: void; result: RunnerServiceOutcome };
  /** Delete the service. When the service is what runs this runner, it is
   *  left loaded, so this runner keeps running until the next restart. */
  serviceRemove: { payload: void; result: RunnerServiceOutcome };
  /** The runner's parts and what each is doing. Owner only, runner-wide. */
  partsList: { payload: void; result: RunnerPartsState };
  /** Start one part's or one module's action in the background and answer
   *  at once with the list; the part's (or module's) `job` then reports it. */
  partsAct: {
    payload: RunnerPartsActPayload;
    result: RunnerPartsState;
  };
  /** Free up space: a dry run by default, a run with `confirm`. Owner only,
   *  and a run is refused on the relay. */
  machineCleanup: {
    payload: MachineCleanupPayload;
    result: RunnerCleanup;
  };
  /** The jobs the default branch declares (.agents/skills/deploy/jobs.json). */
  jobsList: {
    payload: void;
    result: { jobs: RunnerJobInfo[] };
  };
  /** Call a job by name. Never a command: unknown jobs and params that fail
   *  their regex are refused. */
  jobsCall: {
    payload: { job: string; params?: Record<string, string> };
    result: { callId: string };
  };
  /** Cancel a call: a queued or gate-waiting one leaves the queue, a running
   *  one gets SIGTERM (SIGKILL after 10 s). Only the actor who started it or
   *  the runner owner may. `cancelled: false` = it had already ended. */
  jobCancel: {
    payload: { callId: string };
    result: { cancelled: boolean };
  };
  /** Calls that have not ended (queued, waiting-auth, running), from every
   *  path. The owner gets all of them; a member channel only its own. */
  jobsActive: {
    payload: void;
    result: {
      calls: {
        callId: string;
        job: string;
        state: RunnerJobState;
        actor: { userId: string; name: string };
        startedAt: string;
      }[];
    };
  };
  /** A call's state plus its redacted log after `afterSeq`. */
  jobStatus: {
    payload: { callId: string; afterSeq?: number };
    result: {
      state: RunnerJobState;
      exitCode: number | null;
      chunks: { seq: number; text: string }[];
    };
  };
  /** Read the audit log, newest first (owner only). `chainIntact` covers the
   *  whole file, not only the page returned. */
  /** Start the audit log over (owner only). The old log is moved aside on
   *  the runner, not deleted, and the new one opens with an `audit-clear`
   *  line naming who cleared it. */
  auditClear: {
    payload: Record<string, never>;
    result: { cleared: number };
  };
  auditRead: {
    payload: {
      limit?: number;
      beforeTs?: string;
      actorUserId?: string;
      job?: string;
      key?: string;
    };
    result: {
      entries: AuditEntry[];
      chainIntact: boolean;
      firstBrokenTs: string | null;
    };
  };
  /** Encrypted secret updates parked on the runner, waiting for approval. */
  secretsInboxList: {
    payload: void;
    result: { items: SecretsInboxItem[] };
  };
  /** Approve (needs the password) or deny an inbox item. */
  secretsInboxAnswer: {
    payload: {
      id: string;
      password?: string;
      approve: boolean;
      unlockFor?: number;
    };
    result: { success: boolean };
  };
  deployRunCancel: { payload: void; result: { success: boolean } };
  deployRunDismiss: { payload: void; result: { success: boolean } };
  // Run the merged check-environment.sh: a read-only, secret-free preflight
  // proving this machine can run the whole flow ("good to prod push").
  deployCheckEnvironment: {
    payload: void;
    result: {
      ok: boolean;
      checks: { ok: boolean; label: string; detail: string }[];
      output: string;
    };
  };
  // The check failed: AI installs/configures what the failed checks need
  // (env setup only; no repo writes, no secrets), then re-checks.
  // Long-running; streams via the board log.
  deployFixEnvironment: {
    payload: { provider?: AgentProvider };
    result: BoardAgentActionMap['deployCheckEnvironment']['result'];
  };
  // In-flight/settled check+fix state (engine memory), so a reopened modal
  // reattaches instead of losing a running fix.
  deployEnvState: {
    payload: void;
    result: {
      phase: 'idle' | 'checking' | 'fixing' | 'done';
      logs: CardAILogEntry[];
      result: BoardAgentActionMap['deployCheckEnvironment']['result'] | null;
      error: string;
    };
  };
  deployReviseSkill: {
    payload: { feedback: string; provider?: AgentProvider };
    result: BoardAgentActionMap['deployCreateSkill']['result'];
  };
  // Check out a pushed deploy-skill branch (committed as-is) into review;
  // approved revisions become their own commit on that branch.
  deployLoadSkillBranch: {
    payload: { branch: string };
    result: BoardAgentActionMap['deployCreateSkill']['result'];
  };
  // In-flight creator state (engine memory), so a reopened modal reattaches:
  // phase + buffered logs + the last drafted result.
  deployCreatorState: {
    payload: void;
    result: {
      phase: 'idle' | 'working' | 'review';
      logs: CardAILogEntry[];
      draft: BoardAgentActionMap['deployCreateSkill']['result'] | null;
      error: string;
    };
  };
  // Push the previewed draft to a branch; merging it arms the Deploy button.
  deployApproveSkill: {
    payload: void;
    result: { branch: string };
  };
  deployDiscardSkill: {
    payload: void;
    result: { success: boolean };
  };
  // OneDrive host-run device-code auth: frontend passes the public client_id,
  // the host polls Microsoft and pushes an `onedrive-auth` event on completion.
  onedriveStartDeviceCode: {
    payload: { clientId: string };
    result: {
      userCode: string;
      verificationUri: string;
      expiresIn: number;
    };
  };
  onedriveDisconnect: {
    payload: void;
    /** `subscriptionRemoved` false: Microsoft could not be reached to delete
     *  the change subscription. Absent from an older host. */
    result: { success: boolean; subscriptionRemoved?: boolean };
  };
  /** Force a full re-walk of the OneDrive index (ignores the delta cursor). */
  onedriveReindex: { payload: void; result: { success: boolean } };
  // GitHub host-run device-code auth, same contract as OneDrive above: the
  // frontend passes the public client_id, the host polls GitHub and pushes a
  // `github-auth` event on completion. The token leaves the host once only:
  // on disconnect, to the Factiii server, to be revoked at GitHub.
  githubStartDeviceCode: {
    payload: { clientId: string };
    result: {
      userCode: string;
      verificationUri: string;
      expiresIn: number;
    };
  };
  /** Revokes the token at GitHub (through the Factiii server), then forgets
   *  it on the host whatever came of that. `revoked` false: it is gone here
   *  but may still be valid at GitHub, and the owner should revoke it there.
   *  Absent on a host from before the upstream revoke, which only forgot. */
  githubDisconnect: {
    payload: void;
    result: { success: boolean; revoked?: boolean };
  };
  /** Who the stored GitHub token belongs to; null when not connected. */
  githubIdentity: {
    payload: void;
    result: { login: string; name: string; email: string } | null;
  };
  /** Writable repos for the picker, most-recently-pushed first. */
  githubListRepos: {
    payload: void;
    result: {
      fullName: string;
      cloneUrl: string;
      defaultBranch: string;
      private: boolean;
    }[];
  };
  githubListBranches: {
    payload: { fullName: string };
    result: string[];
  };
  // One-click Claude sign-in: start drives `claude auth login` on the host
  // and returns the OAuth URL to open; the pasted code goes back via
  // claudeLoginCode; the outcome arrives on the `claude-auth` event.
  claudeLoginStart: { payload: void; result: { url: string } };
  claudeLoginCode: {
    payload: { code: string };
    result: { success: boolean };
  };
  claudeLoginCancel: { payload: void; result: { success: boolean } };
  claudeLogout: { payload: void; result: { success: boolean } };
  // One-click Codex sign-in: start drives `codex login --device-auth` on the
  // host and returns the verification URL + one-time code to show. The CLI
  // polls OpenAI itself; the outcome arrives on the `codex-auth` event.
  codexLoginStart: { payload: void; result: { url: string; code: string } };
  codexLoginCancel: { payload: void; result: { success: boolean } };
  codexLogout: { payload: void; result: { success: boolean } };
  // The sign-ins above with no board (an empty spaceSlug): Oakbox > Account >
  // Backend's, owner only and never on the relay. The same CLI homes as a
  // board's (agent-homes.ts), so one sign-in serves both. A runner-wide link
  // gets no events, so this is read instead: whether each CLI is signed in,
  // and how its last sign-in from Oakbox went.
  cliSignins: { payload: void; result: RunnerCliSignins };
  // The X on a Running jobs row. Only a sign-in or a failed Ask may be closed
  // (activityDismissable); closing a sign-in cancels its flow too.
  activityDismiss: { payload: { id: string }; result: { success: boolean } };
  cardFinalize: {
    payload: {
      postId: string;
      apply: boolean;
      branchName?: string;
      commitMessage?: string;
    };
    result: { applied: boolean; branchName: string };
  };
  cardAnswerQuestion: {
    payload: { postId: string; answer: string };
    result: void;
  };
  cardFeedback: {
    payload: { postId: string; feedback: string };
    result: void;
  };
  cardKill: { payload: { postId: string }; result: void };
  /** Stop a card's headless agent but keep its worktree + conversation so a
   *  terminal can resume the exact thread (terminal takeover). */
  cardHandoff: { payload: { postId: string }; result: void };
  /** Stop the board-level Ask agent and return its resume id + provider, so the
   *  client opens a terminal (on the board key) that resumes the conversation. */
  boardHandoff: {
    /** Which conversation to hand over; none means the most recently used. */
    payload: { conversationId?: string };
    result: { sessionId: string | null; provider: AgentProvider };
  };

  // ── Bare mode: file ops in the card's workspace ──
  bareFsList: {
    payload: { postId: string; path: string };
    result: BareFileNode[];
  };
  bareFsRead: {
    payload: { postId: string; path: string };
    result: { content: string; encoding: 'utf8' | 'base64' };
  };
  bareFsWrite: {
    payload: {
      postId: string;
      path: string;
      content: string;
      encoding: 'utf8' | 'base64';
    };
    result: void;
  };
  bareFsDelete: { payload: { postId: string; path: string }; result: void };
  bareFsMkdir: { payload: { postId: string; path: string }; result: void };

  /** Drag-and-drop an image onto the terminal: writes the base64 image to the
   *  per-space drops dir and returns the absolute host path to paste.
   *
   *  Sent in chunks, because socket.io drops any single packet over 1MB and a
   *  retina screenshot is bigger than that. Every chunk carries the same
   *  `dropId` and appends to the same file; only the `last` one returns the
   *  path (earlier ones return ''). */
  terminalDropImage: {
    payload: {
      postId: string;
      content: string;
      ext?: string;
      /** Client-generated UUID naming the file this drop appends to. Absent
       *  from a client older than the chunked protocol, which sends the whole
       *  image in one call. */
      dropId?: string;
      /** Closes the drop: prunes old files and returns the path. */
      last?: boolean;
    };
    result: string;
  };

  /** Drag-and-drop or paste any file onto the terminal: appends the base64
   *  chunk to the per-space drops dir and returns the absolute host path to
   *  type (unquoted; the client quotes it with `shellQuotePath`).
   *
   *  Chunked like `terminalDropImage`: every chunk carries the same `dropId`,
   *  `name` and `size`, and only the `last` one returns the path (earlier
   *  ones return ''). The runner keeps the file's own name, made safe, and
   *  picks a free one when the drops dir already holds it. A runner that
   *  predates this answers "Unknown board-agent action". */
  terminalDropFile: {
    payload: {
      postId: string;
      content: string;
      /** Client-generated UUID naming the upload these chunks append to. */
      dropId: string;
      /** The file's name on the client. Untrusted: the runner sanitises it. */
      name: string;
      /** Whole file, decoded bytes. Capped at TERMINAL_DROP_MAX_BYTES; the
       *  chunks may not add up to more, and must add up to exactly this. */
      size: number;
      /** Closes the drop: names the file and returns its path. */
      last?: boolean;
    };
    result: string;
  };

  /** All repo file paths (git-tracked + untracked, gitignore-respecting) for
   * the editor's quick file search. Paths are `/`-prefixed repo-relative. */
  bareFsSearch: {
    payload: { postId: string };
    result: string[];
  };

  /**
   * Snapshot the working tree's git diff against HEAD and move the session into
   * `bare-review`. Frees the user to confirm + push via `cardFinalize`.
   */
  bareReview: {
    payload: { postId: string };
    result: {
      diff: string;
      changedFiles: string[];
      commitMessage: string;
    };
  };

  /**
   * Lightweight peek at the workspace's git state. Does NOT change session
   * phase - used by the git side panel to live-poll while the user is still
   * editing. Covers all three things the panel shows: what is staged, what is
   * not, and what is already committed.
   */
  bareGitStatus: {
    payload: { postId: string };
    result: {
      /** Everything uncommitted (index + working tree) against HEAD. Kept
       *  because a mobile build older than the staged/unstaged split still
       *  reads it. */
      diff: string;
      changedFiles: string[];
      /** Everything below is absent when an older runner answers the call, so
       *  a new client stays readable against one. */
      branch?: string;
      staged?: { files: BareGitFileStatus[]; diff: string };
      unstaged?: { files: BareGitFileStatus[]; diff: string };
      /** Newest first. */
      commits?: BareGitCommit[];
    };
  };

  /** The patch one commit introduced, fetched when the panel expands it -
   *  history is cheap to list and expensive to diff, so the diff waits for a
   *  click. */
  bareGitCommitDiff: {
    payload: { postId: string; sha: string };
    result: { diff: string; changedFiles: string[] };
  };

  /**
   * What this workspace's branch changed against another branch: every file
   * it touched since the two diverged, committed or not pushed alike. The
   * panel's own lists show only what is uncommitted, so a branch whose work is
   * all committed reads as "no changes" without this.
   *
   * One call answers the picker too. `base` absent means "the default": the
   * branch the session was cut from. An empty `base` in the result means there
   * was nothing to compare with.
   */
  bareGitCompare: {
    payload: { postId: string; base?: string };
    result: {
      base: string;
      /** Every branch that can be picked, local and remote. */
      branches: string[];
      files: BareGitFileStatus[];
      diff: string;
      /** Commits here that `base` lacks, and the other way round. */
      ahead: number;
      behind: number;
    };
  };

  /**
   * Move paths into the index (`git add`). An empty `paths` stages everything,
   * which is what the section header's + does.
   *
   * Returns the panel's next state rather than void: staging touches only
   * .git/index, and the working-tree watcher prunes .git, so nothing would
   * tell the panel to re-read.
   */
  bareGitStage: {
    payload: { postId: string; paths: string[] };
    result: BoardAgentActionMap['bareGitStatus']['result'];
  };

  /** Take paths back out of the index. Empty `paths` unstages everything. */
  bareGitUnstage: {
    payload: { postId: string; paths: string[] };
    result: BoardAgentActionMap['bareGitStatus']['result'];
  };

  /**
   * List terminal tmux session tab IDs that exist for this card, paired with
   * the label the tab strip shows: the title the foreground program publishes
   * (`pane_title` - claude puts its current task there), falling back to the
   * process name (`pane_current_command` - `bash`, `vim`, ...) when it sets
   * none, so tabs read like VSCode's. Source of truth across modal
   * close/reopen.
   */
  bareListTerminals: {
    payload: { postId: string };
    result: {
      id: string;
      name: string;
      /** tmux is holding the pane open after its process exited
       *  (`remain-on-exit`), so the tab survived a stray `exit`. */
      dead: boolean;
      // No per-tab `stale`: it cost a digest read on the tab strip's poll to
      // draw a dot that was up on every tab. `bareTerminalHealth` answers
      // staleness for a whole board instead.
    }[];
  };

  /**
   * Whether this board's terminals still match what the runner ships. Answers
   * the "your update needs a restart" prompt: `staleTabs` is what a refresh
   * would restart, and `acked` is true once the user has said "skip for this
   * version" to this exact digest.
   *
   * Digest, not runner version: a release that changes nothing about terminals
   * asks nobody, and one that does is caught without anyone maintaining a list.
   */
  bareTerminalHealth: {
    payload: { postIds: string[] };
    result: {
      digest: string;
      acked: boolean;
      staleTabs: { postId: string; tabId: string }[];
    };
  };

  /** Record "skip for this version" for the digest the runner is on now, so
   *  the prompt stays down until something about terminals actually changes
   *  again. */
  bareAckTerminalDigest: {
    payload: Record<string, never>;
    result: void;
  };

  /** Restart every stale tab on these posts, keeping each tab. Returns how
   *  many were restarted so the client can say what happened. */
  bareRestartStaleTerminals: {
    payload: { postIds: string[] };
    result: { restarted: number };
  };

  /**
   * Snapshot of the runner-side IDE state: which files are open in the
   * editor tab bar and which of those have unsaved buffers. Used by the
   * client to hydrate on modal mount / browser refresh - the runner is the
   * source of truth so closing the modal doesn't lose tabs or edits.
   */
  bareEditorState: {
    payload: { postId: string };
    result: {
      openedFiles: string[];
      buffers: Record<string, string>;
    };
  };

  /** Add `path` to the runner's opened-files list (idempotent). */
  bareOpenFile: {
    payload: { postId: string; path: string };
    result: void;
  };

  /** Remove `path` from openedFiles and discard any buffered edits for it. */
  bareCloseFile: {
    payload: { postId: string; path: string };
    result: void;
  };

  /**
   * Write the editor's current buffer to the runner. Distinct from
   * `bareFsWrite` - this only holds unsaved edits in memory; the file on
   * disk isn't touched until the user explicitly saves.
   */
  bareBufferSet: {
    payload: { postId: string; path: string; content: string };
    result: void;
  };

  /**
   * Everything the card's terminals are running, with the ports each process
   * listens on. Polled while the task manager is open, so it stays a read: it
   * starts nothing and kills nothing.
   */
  bareListProcesses: {
    payload: { postId: string };
    result: BareProcess[];
  };

  /**
   * Ask one of this card's processes to stop (SIGTERM). Refuses any pid that
   * is not in the card's own tmux process tree, so the panel can never signal
   * something that belongs to the machine or to another card.
   */
  bareKillProcess: {
    payload: { postId: string; pid: number };
    result: void;
  };

  /**
   * Open a public tunnel to a local port via frpc (runs factiii-expose).
   * Returns the live tunnel incl. its public URL.
   */
  bareExpose: {
    payload: { postId: string; port: number; name: string };
    result: ExposedPort;
  };

  /** Tear down a named tunnel (runs factiii-unexpose). */
  bareUnexpose: {
    payload: { postId: string; name: string };
    result: void;
  };

  /** The session's currently-open port tunnels. */
  bareListExposed: {
    payload: { postId: string };
    result: ExposedPort[];
  };

  // ── Scratchpad: the space's display surface (see .specs/scratchpad.md) ──
  //
  // Space-scoped, not per session: a scratchpad outlives the terminal that
  // drew on it, and every session on the board looks at the same one.
  scratchpadList: { payload: Record<string, never>; result: ScratchpadItem[] };
  scratchpadRead: {
    payload: { name: string };
    result: { content: string; encoding: 'base64' };
  };
  /** One byte range, for downloading an item the preview cap refuses to
   *  inline. Reading a 200MB recording as a single base64 string would pin the
   *  channel and the browser's memory; a range at a time streams. */
  scratchpadReadChunk: {
    payload: { name: string; offset: number; length: number };
    result: { content: string; encoding: 'base64' };
  };
  scratchpadDelete: { payload: { name: string }; result: void };
  scratchpadClear: { payload: Record<string, never>; result: void };

  // ── Vault backups: the owner's phone vault, held by the runner ──
  //
  // Runner-wide and owner-only (see .specs/runner-vault-backup.md): sent with
  // an empty `spaceSlug`, answered by the daemon itself and not by a board's
  // engine, and never open to a member whatever their level. The bytes
  // are the passphrase-protected package the phone also uploads to our bucket;
  // no passphrase and no key ever travels with them.

  /** Append one base64 chunk to an upload. `offset` is how many bytes the
   *  client has already sent (0 starts the upload); a runner holding any other
   *  amount drops the upload and refuses. On `last` the runner checks `size`,
   *  moves the file into place under a name it picks, prunes old packages and
   *  answers with that name; before that `name` is null. */
  vaultBackupWrite: {
    payload: {
      uploadId: string;
      offset: number;
      content: string;
      last?: boolean;
      /** The whole package's byte count. Checked on `last`. */
      size?: number;
    };
    result: { name: string | null; received: number };
  };
  /** The packages this runner holds, newest first. */
  vaultBackupList: {
    payload: Record<string, never>;
    result: { backups: RunnerVaultBackupItem[] };
  };
  /** One byte range of a package, like `scratchpadReadChunk`. */
  vaultBackupReadChunk: {
    payload: { name: string; offset: number; length: number };
    result: { content: string; encoding: 'base64' };
  };
  vaultBackupDelete: { payload: { name: string }; result: void };

  // ── The Vault (.specs/desktop-ai.md "The Vault") ──
  //
  // Runner-wide like the backups: an empty `spaceSlug`, answered by the
  // daemon before any board. Open to the owner and to the members the owner
  // let in (the server vouches the share, the runner holds its own list).
  // Never carried by the server's relay: vault files only travel on a direct
  // link.

  vaultStatus: { payload: Record<string, never>; result: RunnerVaultStatus };
  /** Newest first. `query` (2+ characters) narrows to files whose path or
   *  text has the words, as the cloud vault's `files` does. `before` is the
   *  last row's id, for the next page. */
  vaultFiles: {
    payload: { query?: string; limit?: number; before?: string };
    result: { files: RunnerVaultFile[]; more: boolean };
  };
  /** Oakbox's search, run on the runner (.specs/desktop-ai.md "Search
   *  across vaults"): the words in a file's name or text first; with none,
   *  the query embedded here and ranked by `<=>`. The query never leaves the
   *  direct link. */
  vaultSearch: {
    payload: { query: string; limit?: number };
    result: { hits: RunnerVaultHit[]; mode: RunnerVaultSearchMode };
  };
  /** A signed write for one file (an R2-style presigned PUT). */
  vaultUploadStart: {
    payload: { name: string; size: number; contentType: string };
    result: { ticket: string };
  };
  /** One base64 range of a signed write. The last one stores the file and
   *  answers with its row. */
  vaultWrite: {
    payload: {
      ticket: string;
      offset: number;
      content: string;
      last?: boolean;
    };
    result: { received: number; file: RunnerVaultFile | null };
  };
  /** A signed read for one file (an R2-style presigned GET). */
  vaultDownload: {
    payload: { uploadId: string };
    result: { ticket: string; size: number; name: string; contentType: string };
  };
  vaultRead: {
    payload: { ticket: string; offset: number; length: number };
    result: { content: string; encoding: 'base64' };
  };
  /** The caller's own files only; another account's are kept and counted. */
  vaultDelete: {
    payload: { uploadIds: string[] };
    result: { deleted: number; kept: number };
  };
  /** Owner only: let a vault peer in, or take them out. */
  vaultShareSet: {
    payload: { memberId: string; shared: boolean };
    result: RunnerVaultStatus;
  };
  /** Oakbox Ask on the runner (.specs/desktop-ai.md "Ask with a runner's
   *  model"): what the caller may ask with. Claude and Codex for the owner
   *  only. */
  vaultAskModels: {
    payload: Record<string, never>;
    result: RunnerAskModels;
  };
  /** Start one turn: search the Vault, then answer with the picked model.
   *  `history` is the thread so far (the app holds it). Answers at once with
   *  the turn's id; `vaultAskPoll` reads it as it is written. */
  vaultAsk: {
    payload: {
      engine: RunnerAskEngine;
      /** The Ollama tag; none for Claude or Codex. */
      model?: string;
      question: string;
      history?: { role: 'you' | 'answer'; text: string }[];
      /** The runner chat this question continues; none starts one. The
       *  runner then answers from the chat's own messages, not `history`. */
      chatId?: string;
      /** Files added to this question in Ask, by their Vault id: read
       *  before anything the search finds. */
      fileIds?: string[];
      /** Claude's or Codex's model and effort from Account > Backend; none
       *  runs the CLI's defaults. */
      cli?: CliAskSettings;
      /** AI Research's factiii id from Factiii's tree: the tag a note the
       *  model keeps this turn carries. None keeps it untagged. */
      aiResearchId?: number | null;
      /** Factiii's category tree, for the tag and category tools; none
       *  leaves them out. */
      categories?: RunnerCategoryTree;
    };
    /** `chatId`: the chat the turn is saved in, new or continued. */
    result: { askId: string; chatId: string };
  };
  /** The turn so far. Only the account that started it reads it. */
  vaultAskPoll: {
    payload: { askId: string };
    result: RunnerAskState;
  };
  /** Stop a running turn: its Claude or Codex process tree is killed, or its
   *  Ollama request aborted, and nothing of its answer is saved. The account
   *  that started it, or the owner. Answers the turn once it has stopped (a
   *  turn already done is answered as it ended). */
  vaultAskCancel: {
    payload: { askId: string };
    result: RunnerAskState;
  };
  /** The caller's Ask chats on this runner, newest first. Kept in the
   *  runner's Vault database, never on Factiii's server. */
  vaultChats: {
    payload: Record<string, never>;
    result: { chats: RunnerAskChat[] };
  };
  /** One of the caller's chats, with its messages. */
  vaultChat: {
    payload: { chatId: string };
    result: { chat: RunnerAskChat; messages: RunnerAskChatMessage[] };
  };
  /** Delete one of the caller's chats, for good. */
  vaultChatDelete: {
    payload: { chatId: string };
    result: { deleted: boolean };
  };
  /** Categorize on the runner (.specs/desktop-ai.md "Categorize on the
   *  runner"), owner only: the category tree comes with the start, the tags
   *  stay on the runner. */
  vaultCategorizeStatus: {
    payload: Record<string, never>;
    result: RunnerCategorizeStatus;
  };
  /** Start one run over every file waiting. Answers at once; one at a time. */
  vaultCategorizeStart: {
    payload: RunnerCategorizePayload;
    result: RunnerCategorizeStatus;
  };
  /** Stop the run: the model call in flight is aborted, and its file stays
   *  waiting. */
  vaultCategorizeCancel: {
    payload: Record<string, never>;
    result: RunnerCategorizeStatus;
  };
  /** One file's page, owner only: what was read off it and its categories
   *  (.specs/desktop-ai.md "A runner file's page"). */
  vaultFilePost: {
    payload: { uploadId: string };
    result: RunnerVaultFilePost;
  };
  /** The notes kept from runner Ask answers, newest first, owner only.
   *  With `noteId`, that one note alone (a search hit opening it). Ask's
   *  model keeps them with its save tool (vault/ask.ts). */
  vaultNotes: {
    payload: { noteId?: string };
    result: { notes: RunnerVaultNote[] };
  };
  /** Delete one note for good. */
  vaultNoteDelete: {
    payload: { noteId: string };
    result: { deleted: boolean };
  };
  /** Oakbox > Account > Backend's models (.specs/desktop-ai.md "Oakbox's
   *  backend"): what Ollama holds, the table with its fit, machine health
   *  and the last pull or remove. Owner only, runner-wide, never on the
   *  relay. */
  localAiList: {
    payload: Record<string, never>;
    result: RunnerLocalAiModels;
  };
  /** Start pulling any Ollama model, or removing one, in the background,
   *  and answer at once; the list's `job` then reports it. */
  localAiAct: {
    payload: LocalAiActPayload;
    result: RunnerLocalAiModels;
  };
  /** Search ollama.com from the runner, never from Factiii's server: a
   *  query's models, or one model's tags with whether each fits. */
  localAiSearch: {
    payload: LocalAiSearchPayload;
    result: RunnerLocalAiSearch;
  };

  // ── Simulator: the space's iOS simulator panel ──
  //
  // Space-scoped like the scratchpad, and for the same reason: the simulator
  // belongs to the machine, not to whichever terminal happens to be open on it.

  simulatorList: { payload: Record<string, never>; result: SimulatorDevice[] };
  /**
   * Which simulators are booted.
   *
   * A set, not "the booted one": several can run at once, and the panel's
   * auto-open has to fire when a *new* one starts rather than when any is up.
   */
  simulatorStatus: {
    payload: Record<string, never>;
    result: { booted: string[] };
  };
  /** Start pushing frames. Re-attaching with the same options is a no-op; new
   *  options restart the encoder, whose output size is fixed at session start. */
  simulatorAttach: {
    payload: { udid: string; width: number; fps: number; bitrate: number };
    result: void;
  };
  simulatorDetach: { payload: Record<string, never>; result: void };
  simulatorInput: { payload: SimulatorInput; result: void };

  // ── Desktop view: the Mac's own screen (see .specs/desktop-view.md) ──
  //
  // The owner, and a member at the Screens level: it is the whole machine,
  // not a board. Video rides the peer connection's one video track, so it and
  // the simulator take turns, and one person holds it at a time.

  /** Whether this runner can show its screen and which macOS permissions it
   *  has. `request` asks macOS to prompt, which needs someone at the Mac. */
  screenStatus: { payload: { request?: boolean }; result: ScreenStatus };
  /** Start pushing frames. Answers with the status: without Screen Recording
   *  nothing starts and `streaming` is false. */
  screenAttach: {
    payload: { width: number; fps: number; bitrate: number };
    result: ScreenStatus;
  };
  screenDetach: { payload: Record<string, never>; result: void };
  screenInput: { payload: ScreenInput; result: void };

  // ── Credential store (runner-wide, owner only) ──
  //
  // Not board-scoped: the store spans every board on the machine, so a member
  // the runner is shared with cannot act on it. The daemon enforces that
  // before dispatch. The connection password itself is gone; these names all
  // still answer because older clients send them (runner secureActions.ts).

  /** Current protection state, including the fix text when it isn't usable. */
  secureStatus: { payload: void; result: SecureStoreStatus };
  /** The one use the old connection password still has: release the files an
   *  older runner locked behind it. They are converted and the password is
   *  removed, so the status that comes back usually has none. */
  secureUnlock: { payload: { password: string }; result: SecureStoreStatus };
  /** Nothing to lock any more: answers with the status and changes nothing. */
  secureLock: { payload: void; result: SecureStoreStatus };
  /** Always refused: a connection password can no longer be set or changed. */
  secureSetPassword: {
    payload: { current?: string; password: string };
    result: SecureStoreStatus;
  };
  /** Same as secureUnlock: retiring the password is what the unlock does. */
  secureClearPassword: {
    payload: { password: string };
    result: SecureStoreStatus;
  };
};

/** What a runner says about showing its own screen. */
export type ScreenStatus = {
  /** False off a Mac, and on a runner with no helper source to build. */
  supported: boolean;
  /** macOS "Screen Recording" is on for the helper: there is a picture. */
  screen: boolean;
  /** macOS "Accessibility" is on for the helper: clicks and keys land. */
  control: boolean;
  streaming: boolean;
};

/** One input event for the desktop view. Pointer positions are 0..1 ratios of
 *  the main display, so nothing on the wire depends on its size. */
export type ScreenInput =
  | {
      type: 'pointer';
      phase: 'move' | 'down' | 'up';
      x: number;
      y: number;
      button?: 'left' | 'right';
    }
  | { type: 'scroll'; dx: number; dy: number }
  /** A USB HID keyboard usage, as the simulator panel sends. */
  | { type: 'key'; code: number; down: boolean }
  | { type: 'text'; text: string };

export type BareFileNode = {
  name: string;
  /** Path relative to the repo root. */
  path: string;
  type: 'file' | 'dir';
};

export type ExposedPort = {
  name: string;
  port: number;
  url: string;
};

/** One process a card's terminals are running, as the task manager lists it.
 *  Everything a user starts in a tab is a descendant of that tab's shell, which
 *  is what ties a process to a card rather than to the machine. */
export type BareProcess = {
  pid: number;
  /** The tab it belongs to, numbered as bareListTerminals numbers them. */
  tabId: string;
  /** The binary, without its path: 'node', 'claude', 'vite'. */
  command: string;
  /** The full command line. */
  args: string;
  /** Percent of one core, as ps reports it. */
  cpu: number;
  /** Resident memory in KB. */
  memoryKb: number;
  /** How long it has been up: [[dd-]hh:]mm:ss. */
  elapsed: string;
  /** TCP ports it accepts connections on. Empty for most processes. */
  ports: number[];
  /** Connections currently established. The honest stand-in for bandwidth,
   *  which macOS will not report per-process without elevated privileges. */
  connections: number;
};

/**
 * A coalesced batch of working-tree changes made by someone other than the
 * editor - the card's agent, the terminal, or a git operation. Paths are
 * repo-relative and `/`-prefixed, same as `BareFileNode.path`.
 */
export type BareFsChange = {
  added: string[];
  changed: string[];
  removed: string[];
  /** Too many paths to be worth patching (a checkout, an install, a build):
   *  every list is empty and the client should refetch what it displays. */
  resync: boolean;
  /** The watch died under a live session (the OS ran out of watches). What's
   *  on screen is frozen from here, so the UI falls back to a manual refresh
   *  rather than pretending to be live. A session ENDING is silent - its
   *  session-update already says so. */
  stopped?: boolean;
};

export type BoardAgentAction = keyof BoardAgentActionMap;

/** Discriminated union - switch on `msg.action` narrows `msg.payload` automatically. */
export type BoardAgentMessage = {
  [A in BoardAgentAction]: {
    action: A;
    spaceSlug: string;
    // Stable board id (slugs can change).
    // Optional for back-compat with older clients that only send the slug;
    // the engine falls back to slug-based naming when absent.
    spaceId?: number;
    payload: BoardAgentActionMap[A]['payload'];
  };
}[BoardAgentAction];

export type BoardAgentEventMap = {
  log: CardAILogEntry;
  status: string;
  'setup-status': string;
  'session-update': CardAISessionSummary;
  'session-removed': { postId: string };
  // Space-level work that isn't a card session (Ask Board, deploy, setup,
  // indexing, sign-ins). `state: null` retires the row.
  'activity-update': RunnerActivity;
  // Pushed by the host when the device-code flow finishes (it polls Microsoft
  // itself, then alerts the client once - no client-side polling).
  'onedrive-auth': { status: OneDrivePollStatus; message?: string };
  // Pushed by the host when the GitHub device-code flow finishes. Carries the
  // resolved identity so the UI can show "Connected as @x" without a refetch.
  'github-auth': {
    status: 'complete' | 'expired' | 'declined' | 'error';
    message?: string;
    login?: string;
  };
  // Pushed by the host when the one-click Claude sign-in finishes (the host
  // drives the CLI itself; the client only opens the URL and relays the code).
  'claude-auth': { status: 'complete' | 'error'; message?: string };
  // Pushed by the host when the one-click Codex device-auth finishes (the CLI
  // polls OpenAI itself; the client only shows the URL + code).
  'codex-auth': { status: 'complete' | 'error'; message?: string };
  // Creator session settled (drafted or failed) - a reopened modal refetches
  // deployCreatorState on this signal.
  'deploy-creator': { phase: 'review' | 'error'; error?: string };
  // Deploy run transitioned; clients refetch deployRunState.
  'deploy-run': { phase: string };
  // Env check/fix settled; clients refetch deployEnvState.
  'deploy-env': { phase: 'done' | 'error' };
  // A command somewhere in this space is waiting on the owner's store
  // password, or has stopped waiting. Carries only whether something is
  // pending; the detail comes from secretsAuthState, so a stale broadcast
  // cannot leave a prompt on screen for an answered request.
  'secrets-auth': { pending: boolean };
  /** Sent to one member when the owner changed their level on this board. */
  'runner-access': RunnerAccessState;
  // Spotlight-style OneDrive index progress, pushed as a pass runs.
  'onedrive-index': {
    status: 'indexing' | 'idle' | 'error';
    done: number;
    error?: string;
  };
  // The space's scratchpad changed on disk. Carries no paths: the panel holds a
  // whole flat listing and refetching it is one small call.
  scratchpad: { at: number };
  [key: `log:${string}`]: CardAILogEntry;
  // Working-tree changes for one bare session, keyed by its postId so an open
  // modal only wakes for its own card.
  [key: `bare-fs:${string}`]: BareFsChange;
};

// Setup status lines share one channel (emitSetupStatus). A line carrying this
// prefix is streamed LOG output and accumulates in the setup log block; a plain
// line is the current step label and replaces the previous one. Same split the
// Docker image build used, when BuildKit's own "#" prefix did the marking.
export const SETUP_LOG_PREFIX = '\u0001log ';

// Initialize needs the owner's sudo password to install a system package (or
// to bootstrap Homebrew). A line with this prefix carries the reason and pauses
// setup until `setupAuthorize` answers. ONLY the runner's own toolchain
// commands ever ask: the agent protocol has no path to sudo, deliberately.
export const SETUP_AUTH_PREFIX = '\u0001sudo ';

// ── Bare mode: interactive shell into the card's workspace ──
// Multiplexed over the existing IPC/data-channel transport. Each terminal
// is identified by an opaque sessionId minted by the client; the runner
// does not need to remember it across reconnects.

export type TerminalClientMessage =
  | {
      type: 'terminal';
      op: 'open';
      sessionId: string;
      postId: string;
      cols: number;
      rows: number;
      /**
       * Stable tab key. Maps to a named tmux session, so
       * closing/reopening the modal reattaches to the same shell.
       */
      terminalKey: string;
      /**
       * When set, this tab boots straight into the card's agent conversation
       * (`claude --resume <id>` / `codex resume <id>`) instead of a bare
       * prompt. Only honoured on tmux session *creation* — reopening the
       * modal reattaches to the already-running agent rather than launching
       * a second one. Exiting the agent drops to a normal shell.
       */
      resumeAgentSessionId?: string;
      /** Which CLI owns `resumeAgentSessionId`. Defaults to claude. */
      resumeProvider?: AgentProvider;
      /**
       * This tab belongs to a card and should start a NEW agent pointed at it.
       * The card's text is never sent: the runner writes an opening prompt
       * naming the card's uuid, and the agent reads it through the
       * factiii-board MCP.
       *
       * Ignored when `resumeAgentSessionId` is set — a resumed thread already
       * knows the card — and, like resume, only honoured on tmux session
       * creation.
       */
      briefCard?: boolean;
    }
  | { type: 'terminal'; op: 'stdin'; sessionId: string; data: string }
  | {
      type: 'terminal';
      op: 'resize';
      sessionId: string;
      cols: number;
      rows: number;
    }
  | { type: 'terminal'; op: 'close'; sessionId: string }
  /** Destroy the underlying tmux session entirely (used when the user
   *  explicitly closes a tab; plain `close` only detaches). */
  | {
      type: 'terminal';
      op: 'kill';
      postId: string;
      terminalKey: string;
    }
  /** Kill what the tab is running and start a fresh shell in the SAME tmux
   *  session, so the tab keeps its place while the processes in it die.
   *  Unlike `kill`, the tab survives; unlike `close`, the shell does not. */
  | {
      type: 'terminal';
      op: 'restart';
      postId: string;
      terminalKey: string;
    };

export type TerminalServerMessage =
  | { type: 'terminal'; op: 'opened'; sessionId: string }
  | { type: 'terminal'; op: 'stdout'; sessionId: string; data: string }
  | {
      type: 'terminal';
      op: 'exit';
      sessionId: string;
      code: number | null;
      reason?: string;
    }
  | { type: 'terminal'; op: 'error'; sessionId: string; message: string };

/** The name the client opens its detached terminal window under. Shared: the
 *  desktop shell keys on it to tell an app window from a clicked link. */
export const TERMINAL_WINDOW_NAME = 'factiii-terminal';

/** A backup file, its passphrase, and the password of the board it is
 *  imported into. */
export type SecretsBackupImportRequest = {
  bundle: string;
  passphrase: string;
  /** The password of the board the backup is imported into. */
  targetPassword: string;
};

/** One secret a backup holds, picked for import. `conflict` decides a name
 *  the board already holds with another value. */
export type BackupImportPick = { name: string; conflict?: 'keep' | 'take' };

/** A backup beside the board it would be imported into. Never a value: a
 *  masked tail for a key, a size for a file. `current` is the board's own
 *  secret of that name, null when it has none; `same` means the two are equal,
 *  so there is nothing to choose. */
export type SecretsBackupImportPlan = {
  board: string;
  createdAt: string;
  secrets: {
    name: string;
    masked: string;
    current: { masked: string; same: boolean } | null;
  }[];
  files: {
    name: string;
    size: number;
    current: { size: number; same: boolean } | null;
  }[];
};

/** A backup file, opened (`secretsOpenBackup`). */
export type OpenedSecretsBackup = {
  /** The board the backup says it is of, and when it was made. */
  board: string;
  createdAt: string;
  secrets: { name: string; value: string; description: string }[];
  /** File secrets. `data` is base64, or null when the file is too large to
   *  send to the page. */
  files: { name: string; size: number; data: string | null }[];
};
